import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import UploadDocumentsModal from "./UploadDocumentsModal";
import { validateForm, normalizeNomineesList } from "../../common/utils";
import { journeyActions } from "../../store/journeySlice";
import BrandLoader from "../../common/components/BrandLoader";
import {
  enrichNomineesWithBizOpsCodes,
  toNomineeRelationshipOptions,
} from "./bizOpsNomineeMaps";
import {
  getProposalIncomeProofPendingLabel,
  isProposalDocumentSlotFilled,
} from "../../common/utils/proposalDocumentSlots";
import { TEXT_CONTENT } from "../../constants";
import { useOrganisationListed } from "../../common/hooks/useOrganisationListed";
import { useProposalAddressLocation } from "../../common/hooks/useProposalAddressLocation";
/* ─── Constants ─── */
const EMPTY_NOMINEE = {
  relationship: "",
  titleOfNominee: "",
  fullName: "",
  mobileNo: "",
  dateOfBirth: "",
  gender: "",
  emailId: "",
  allocation: "100",
  mailingAddressLine1: "",
  mailingAddressLine2: "",
  mailingAddressLine3: "",
  pinCode: "",
  state: "",
  city: "",
  communicationAddressLine1: "",
  communicationAddressLine2: "",
  communicationAddressLine3: "",
  communicationPinCode: "",
  communicationState: "",
  communicationCity: "",
  bankName: "",
  branchName: "",
  accountNumber: "",
  appointeeRelationship: "",
  appointeeTitle: "",
  appointeeFullName: "",
  appointeeMobileNo: "",
  appointeeDateOfBirth: "",
  communicationAddressSameAsPermanent: null,
  nomineeAddressSameAsProposer: null,
  appointeeAddressSameAsNominee: null,
  appointeeRequired: null,
};

const EMAIL_DOMAINS = ["gmail.com", "yahoo.com", "outlook.com", "hotmail.com"];

const sanitizeNomineeList = (list) => {
  if (!Array.isArray(list)) return [{ ...EMPTY_NOMINEE }];
  const sanitized = list
    .filter((item) => item && typeof item === "object")
    .map((item) => ({ ...EMPTY_NOMINEE, ...item }));
  return sanitized.length > 0 ? sanitized : [{ ...EMPTY_NOMINEE }];
};

/* ─── Small form helpers ─── */
const FormField = ({ label, error, children, halfWidth, optional }) => (
  <div className={`proposal-field ${halfWidth ? "half" : ""}`}>
    <label className="proposal-field__label label-one m-0 pt-2 pb-1">
      {label}
      {optional && <span className="optional"> (Optional)</span>}
    </label>
    {children}
    {error && <div className="proposal-field__error">{error}</div>}
  </div>
);

const TextInput = ({
  placeholder,
  value,
  onChange,
  name,
  disabled,
  maxLength,
  handleKeyDown,
}) => (
  <input
    type="text"
    className="proposal-input"
    placeholder={placeholder || "Enter"}
    value={value || ""}
    onChange={(e) => onChange(name, e.target.value)}
    disabled={disabled}
    maxLength={maxLength}
    onKeyDown={handleKeyDown}
    name={name}
    autoComplete="new-password"
    autoCorrect="off"
    autoCapitalize="off"
    spellCheck={false}
  />
);

const ensureOptionExists = (options = [], value) => {
  if (!value) return options || [];

  const exists = (options || []).some(
    (opt) => (typeof opt === "string" ? opt : opt.value) === value,
  );

  if (exists) return options || [];

  return [...(options || []), { label: value, value }];
};

const SelectInput = ({
  placeholder,
  value,
  onChange,
  name,
  options,
  disabled,
}) => {
  const mergedOptions = ensureOptionExists(options, value);
  return (
    <select
      className="proposal-input proposal-select"
      value={value || ""}
      onChange={(e) => onChange(name, e.target.value)}
      disabled={disabled}
    >
      <option value="">{placeholder || "Select"}</option>
      {mergedOptions.map((opt) => (
        <option
          key={typeof opt === "string" ? opt : opt.value}
          value={typeof opt === "string" ? opt : opt.value}
        >
          {typeof opt === "string" ? opt : opt.label}
        </option>
      ))}
    </select>
  );
};

const MobileInput = ({ value, onChange, name }) => (
  <div className="proposal-mobile-wrapper">
    <div className="proposal-mobile-prefix">
     <svg
            className="flag-icon"
            width="20"
            height="15"
            viewBox="0 0 20 15"
            fill="none"
          >
            <rect width="20" height="15" fill="#FF9933" />
            <rect y="5" width="20" height="5" fill="#FFFFFF" />
            <rect y="10" width="20" height="5" fill="#138808" />
            {/* Ashoka Chakra: 24-spoke navy wheel on the white stripe */}
            <circle cx="10" cy="7.5" r="2.3" fill="none" stroke="#0000AB" strokeWidth="0.15" />
            <circle cx="10" cy="7.5" r="0.3" fill="#0000AB" />
            <line x1="10.000" y1="7.150" x2="10.000" y2="5.200" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.091" y1="7.162" x2="10.595" y2="5.278" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.175" y1="7.197" x2="11.150" y2="5.508" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.247" y1="7.253" x2="11.626" y2="5.874" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.303" y1="7.325" x2="11.992" y2="6.350" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.338" y1="7.409" x2="12.222" y2="6.905" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.350" y1="7.500" x2="12.300" y2="7.500" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.338" y1="7.591" x2="12.222" y2="8.095" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.303" y1="7.675" x2="11.992" y2="8.650" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.247" y1="7.747" x2="11.626" y2="9.126" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.175" y1="7.803" x2="11.150" y2="9.492" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.091" y1="7.838" x2="10.595" y2="9.722" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.000" y1="7.850" x2="10.000" y2="9.800" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.909" y1="7.838" x2="9.405" y2="9.722" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.825" y1="7.803" x2="8.850" y2="9.492" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.753" y1="7.747" x2="8.374" y2="9.126" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.697" y1="7.675" x2="8.008" y2="8.650" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.662" y1="7.591" x2="7.778" y2="8.095" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.650" y1="7.500" x2="7.700" y2="7.500" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.662" y1="7.409" x2="7.778" y2="6.905" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.697" y1="7.325" x2="8.008" y2="6.350" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.753" y1="7.253" x2="8.374" y2="5.874" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.825" y1="7.197" x2="8.850" y2="5.508" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.909" y1="7.162" x2="9.405" y2="5.278" stroke="#0000AB" strokeWidth="0.15" />
          </svg>
      <span>+91</span>
    </div>
    <input
      type="text"
      className="proposal-input proposal-mobile-input"
      placeholder="e.g. 98762 72383"
      autoComplete="mobile-off"
      value={value || ""}
      onChange={(e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
        onChange(name, digits);
      }}
      maxLength={10}
      inputMode="numeric"
    />
  </div>
);

const ymdToLocalDate = (ymd) => {
  if (!ymd || typeof ymd !== "string") return null;
  const m = ymd.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d))
    return null;
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d)
    return null;
  return dt;
};

const localDateToYmd = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${mo}-${d}`;
};

const formatDdMmYyyyDigits = (digits) => {
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const eightDigitsToIso = (digits8) => {
  if (!digits8 || digits8.length !== 8) return "";
  const dd = digits8.slice(0, 2);
  const mm = digits8.slice(2, 4);
  const yyyy = digits8.slice(4, 8);
  const d = Number(dd);
  const mo = Number(mm);
  const y = Number(yyyy);
  if (!Number.isFinite(d) || !Number.isFinite(mo) || !Number.isFinite(y))
    return "";
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return "";
  const dt = new Date(y, mo - 1, d);
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== mo - 1 ||
    dt.getDate() !== d
  ) {
    return "";
  }
  return `${yyyy}-${mm}-${dd}`;
};

const handleDobRawInputChange = (e, name, onChange) => {
  if (e.nativeEvent instanceof MouseEvent) return;

  const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
  e.target.value = formatDdMmYyyyDigits(digits);

  if (digits.length === 8) {
    const iso = eightDigitsToIso(digits);
    if (iso) onChange(name, iso);
  } else if (digits.length === 0) {
    onChange(name, "");
  }
};

export const DateInput = ({
  value,
  onChange,
  name,
  min,
  max,
  autoComplete,
}) => {
  const selected = ymdToLocalDate(value);
  const minDate = ymdToLocalDate(min) || undefined;
  const maxDate = ymdToLocalDate(max) || undefined;

  return (
    <div className="proposal-dob-datepicker">
      <DatePicker
        selected={selected}
        onChange={(date) => onChange(name, date ? localDateToYmd(date) : "")}
        minDate={minDate}
        maxDate={maxDate}
        dateFormat="dd/MM/yyyy"
        placeholderText="DD/MM/YYYY"
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        scrollableYearDropdown
        yearDropdownItemNumber={100}
        showIcon
        toggleCalendarOnIconClick
        openOnFocus
        className="proposal-input"
        wrapperClassName="proposal-dob-datepicker-inner"
        autoComplete={autoComplete || "off"}
        onChangeRaw={(e) => handleDobRawInputChange(e, name, onChange)}
      />
    </div>
  );
};

const GenderRadio = ({ value, onChange, name }) => (
  <div className="proposal-gender-group radio-group">
    {["Male", "Female"].map((g) => (
      <label
        key={g}
        className={`proposal-gender-option radio-label ${value === g ? "selected" : ""}`}
      >
        <input
          type="radio"
          name={name}
          value={g}
          checked={value === g}
          onChange={(e) => onChange(name, e.target.value)}
        />
        <span>{g}</span>
      </label>
    ))}
  </div>
);

const AllocationInput = ({ value, onChange, name }) => {
  const handleInputChange = (e) => {
    let val = e.target.value;

    if (val === "") {
      onChange(name, "");
      return;
    }

    if (!/^\d+$/.test(val)) return;

    const numericValue = Number(val);

    if (numericValue < 1 || numericValue > 100) return;

    onChange(name, numericValue);
  };

  return (
    <div className="proposal-allocation-wrapper">
      <span className="proposal-allocation-prefix">%</span>
      <input
        type="text"
        className="proposal-input proposal-select proposal-allocation-select"
        value={value ?? ""}
        onChange={handleInputChange}
        maxLength={3}
        placeholder="Enter 1 - 100"
        autoComplete="Allocate-num"
      />
    </div>
  );
};

/* ─── Nominee Illustration ─── */
const NomineeIllustration = () => (
  <div className="proposal-side__illustration">
    <svg
      width="110"
      height="150"
      viewBox="0 0 110 150"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M55.2702 19.6687C55.9163 27.7445 65.2463 38.2337 68.2296 41.407C68.3469 41.5327 68.4889 41.633 68.6466 41.7015C68.8043 41.7701 68.9745 41.8054 69.1464 41.8054C69.3184 41.8054 69.4885 41.7701 69.6463 41.7015C69.804 41.633 69.9459 41.5327 70.0633 41.407C73.0561 38.2337 82.3766 27.7398 83.0227 19.6687C83.0448 19.4208 82.9941 19.172 82.8768 18.9526C82.7595 18.7332 82.5807 18.5528 82.3624 18.4335L69.745 11.626C69.5615 11.5254 69.3557 11.4727 69.1464 11.4727C68.9372 11.4727 68.7313 11.5254 68.5479 11.626L55.9305 18.4335C55.713 18.5537 55.5349 18.7343 55.4177 18.9534C55.3005 19.1726 55.2493 19.421 55.2702 19.6687Z"
        fill="#97262A"
      />
      <path
        opacity="0.3"
        d="M68.2108 38.347C64.4104 34.0715 58.8998 26.884 57.5839 21.1644C57.5241 20.8985 57.5509 20.6206 57.6602 20.371C57.7696 20.1215 57.9559 19.9134 58.1919 19.7772L68.5195 13.8391C68.7112 13.7295 68.9282 13.6719 69.149 13.6719C69.3698 13.6719 69.5867 13.7295 69.7784 13.8391L80.106 19.7582C80.3483 19.8972 80.5382 20.112 80.6464 20.3696C80.7545 20.6271 80.775 20.9131 80.7046 21.1834C79.3935 26.9125 73.9019 34.0715 70.0825 38.366C69.9639 38.4973 69.8188 38.602 69.6567 38.6731C69.4946 38.7441 69.3193 38.78 69.1423 38.7782C68.9654 38.7764 68.7908 38.737 68.6302 38.6627C68.4696 38.5883 68.3266 38.4807 68.2108 38.347Z"
        fill="#97262A"
      />
      <path
        opacity="0.3"
        d="M68.2108 38.347C64.4104 34.0715 58.8998 26.884 57.5839 21.1644C57.5241 20.8985 57.5509 20.6206 57.6602 20.371C57.7696 20.1215 57.9559 19.9134 58.1919 19.7772L68.5195 13.8391C68.7112 13.7295 68.9282 13.6719 69.149 13.6719C69.3698 13.6719 69.5867 13.7295 69.7784 13.8391L80.106 19.7582C80.3483 19.8972 80.5382 20.112 80.6464 20.3696C80.7545 20.6271 80.775 20.9131 80.7046 21.1834C79.3935 26.9125 73.9019 34.0715 70.0825 38.366C69.9639 38.4973 69.8188 38.602 69.6567 38.6731C69.4946 38.7441 69.3193 38.78 69.1423 38.7782C68.9654 38.7764 68.7908 38.737 68.6302 38.6627C68.4696 38.5883 68.3266 38.4807 68.2108 38.347Z"
        fill="white"
      />
      <path
        d="M67.7403 29.68C67.6467 29.6802 67.554 29.6618 67.4676 29.6259C67.3812 29.59 67.3027 29.5374 67.2368 29.471L63.7166 25.9366C63.5908 25.8015 63.5222 25.6228 63.5255 25.4382C63.5288 25.2536 63.6035 25.0775 63.7341 24.9469C63.8647 24.8164 64.0408 24.7416 64.2254 24.7383C64.41 24.7351 64.5887 24.8036 64.7237 24.9295L67.7403 27.9413L74.8661 20.8393C75.0003 20.707 75.1812 20.6328 75.3696 20.6328C75.5581 20.6328 75.739 20.707 75.8732 20.8393C75.9398 20.9051 75.9928 20.9835 76.0289 21.07C76.065 21.1564 76.0836 21.2491 76.0836 21.3428C76.0836 21.4365 76.065 21.5293 76.0289 21.6157C75.9928 21.7021 75.9398 21.7805 75.8732 21.8464L68.2724 29.4472C68.2045 29.5205 68.1222 29.579 68.0306 29.619C67.9391 29.6591 67.8402 29.6798 67.7403 29.68Z"
        fill="white"
      />
      <path
        d="M97.6541 48.8945H31.7787C28.6304 48.8945 26.0781 51.4468 26.0781 54.5951V141.007C26.0781 144.155 28.6304 146.708 31.7787 146.708H97.6541C100.802 146.708 103.355 144.155 103.355 141.007V54.5951C103.355 51.4468 100.802 48.8945 97.6541 48.8945Z"
        fill="#263238"
      />
      <path
        d="M102.092 48.8945H36.2162C33.0679 48.8945 30.5156 51.4468 30.5156 54.5951V141.007C30.5156 144.155 33.0679 146.708 36.2162 146.708H102.092C105.24 146.708 107.792 144.155 107.792 141.007V54.5951C107.792 51.4468 105.24 48.8945 102.092 48.8945Z"
        fill="#FCE4E5"
      />
      <path
        d="M44.0156 146.711H37.6024C37.4056 146.711 37.2461 146.87 37.2461 147.067V148.098C37.2461 148.295 37.4056 148.454 37.6024 148.454H44.0156C44.2123 148.454 44.3719 148.295 44.3719 148.098V147.067C44.3719 146.87 44.2123 146.711 44.0156 146.711Z"
        fill="#263238"
      />
      <path
        d="M100.699 146.711H94.286C94.0892 146.711 93.9297 146.87 93.9297 147.067V148.098C93.9297 148.295 94.0892 148.454 94.286 148.454H100.699C100.896 148.454 101.055 148.295 101.055 148.098V147.067C101.055 146.87 100.896 146.711 100.699 146.711Z"
        fill="#263238"
      />
      <path
        opacity="0.2"
        d="M102.092 48.8945H36.2162C33.0679 48.8945 30.5156 51.4468 30.5156 54.5951V141.007C30.5156 144.155 33.0679 146.708 36.2162 146.708H102.092C105.24 146.708 107.792 144.155 107.792 141.007V54.5951C107.792 51.4468 105.24 48.8945 102.092 48.8945Z"
        fill="#E35055"
      />
      <g
        opacity="0.4"
        //  style="mix-blend-mode:color-dodge"
      >
        <path
          d="M94.33 135.429H43.9746C43.3979 135.427 42.8452 135.198 42.4375 134.79C42.0297 134.382 41.8001 133.83 41.7988 133.253V62.3563C41.7988 61.7788 42.0279 61.2249 42.4358 60.8161C42.8437 60.4073 43.3971 60.177 43.9746 60.1758H94.33C94.9075 60.177 95.4609 60.4073 95.8688 60.8161C96.2767 61.2249 96.5058 61.7788 96.5058 62.3563V133.253C96.5045 133.83 96.2749 134.382 95.8671 134.79C95.4594 135.198 94.9067 135.427 94.33 135.429ZM43.9746 60.4133C43.4601 60.4146 42.9671 60.6198 42.6038 60.9841C42.2404 61.3483 42.0364 61.8418 42.0364 62.3563V133.253C42.0364 133.507 42.0865 133.76 42.1839 133.995C42.2813 134.23 42.4241 134.443 42.604 134.623C42.784 134.803 42.9977 134.946 43.2328 135.044C43.468 135.141 43.72 135.191 43.9746 135.191H94.33C94.8441 135.191 95.3371 134.987 95.7005 134.623C96.064 134.26 96.2682 133.767 96.2682 133.253V62.3563C96.2682 61.8418 96.0642 61.3483 95.7008 60.9841C95.3375 60.6198 94.8445 60.4146 94.33 60.4133H43.9746Z"
          fill="#E6E6E6"
        />
      </g>
      <path
        d="M95.4395 133.254V62.353C95.4395 61.7417 94.9439 61.2461 94.3326 61.2461H43.9724C43.3611 61.2461 42.8655 61.7417 42.8655 62.353L42.8655 133.254C42.8655 133.866 43.3611 134.361 43.9724 134.361H94.3326C94.9439 134.361 95.4395 133.866 95.4395 133.254Z"
        fill="#FCE4E5"
      />
      <path
        opacity="0.1"
        d="M95.4395 133.254V62.353C95.4395 61.7417 94.9439 61.2461 94.3326 61.2461H43.9724C43.3611 61.2461 42.8655 61.7417 42.8655 62.353L42.8655 133.254C42.8655 133.866 43.3611 134.361 43.9724 134.361H94.3326C94.9439 134.361 95.4395 133.866 95.4395 133.254Z"
        fill="#E35055"
      />
      <path
        d="M109.45 69.0625H108.975V73.0719H109.45V69.0625Z"
        fill="#FCE4E5"
      />
      <path
        d="M109.45 54.5938H108.975V64.8359H109.45V54.5938Z"
        fill="#FCE4E5"
      />
      <path
        opacity="0.1"
        d="M69.1504 115.387C78.097 115.387 85.3497 108.134 85.3497 99.1875C85.3497 90.2409 78.097 82.9883 69.1504 82.9883C60.2038 82.9883 52.9512 90.2409 52.9512 99.1875C52.9512 108.134 60.2038 115.387 69.1504 115.387Z"
        fill="black"
      />
      <path
        d="M69.1505 75.1331C71.336 75.1331 73.1077 73.3614 73.1077 71.1759C73.1077 68.9904 71.336 67.2188 69.1505 67.2188C66.9651 67.2188 65.1934 68.9904 65.1934 71.1759C65.1934 73.3614 66.9651 75.1331 69.1505 75.1331Z"
        fill="#FCE4E5"
      />
      <path
        opacity="0.6"
        d="M69.1271 75.1214C71.3126 75.1214 73.0843 73.3497 73.0843 71.1642C73.0843 68.9787 71.3126 67.207 69.1271 67.207C66.9416 67.207 65.1699 68.9787 65.1699 71.1642C65.1699 73.3497 66.9416 75.1214 69.1271 75.1214Z"
        fill="white"
      />
      <path
        d="M69.1512 74.3025C68.533 74.3025 67.9286 74.1191 67.4146 73.7757C66.9006 73.4322 66.4999 72.944 66.2633 72.3728C66.0267 71.8017 65.9648 71.1732 66.0855 70.5668C66.2061 69.9604 66.5038 69.4035 66.9409 68.9663C67.3781 68.5292 67.935 68.2315 68.5414 68.1108C69.1478 67.9902 69.7763 68.0521 70.3474 68.2887C70.9186 68.5253 71.4068 68.926 71.7503 69.44C72.0937 69.954 72.2771 70.5584 72.2771 71.1766C72.2758 72.0053 71.9461 72.7996 71.3602 73.3855C70.7742 73.9715 69.9799 74.3012 69.1512 74.3025ZM69.1512 68.2883C68.58 68.2883 68.0216 68.4577 67.5466 68.7751C67.0716 69.0925 66.7014 69.5435 66.4828 70.0713C66.2642 70.5991 66.207 71.1798 66.3184 71.7401C66.4299 72.3004 66.7049 72.815 67.1089 73.219C67.5128 73.6229 68.0275 73.898 68.5878 74.0094C69.148 74.1209 69.7288 74.0637 70.2565 73.8451C70.7843 73.6265 71.2354 73.2563 71.5528 72.7813C71.8701 72.3063 72.0395 71.7479 72.0395 71.1766C72.0383 70.411 71.7336 69.6771 71.1922 69.1357C70.6508 68.5943 69.9169 68.2896 69.1512 68.2883Z"
        fill="#FCE4E5"
      />
      <path
        d="M42.7995 70.5586H41.0371C40.6488 70.5586 40.334 70.8734 40.334 71.2617V80.9052C40.334 81.2935 40.6488 81.6083 41.0371 81.6083H42.7995C43.1878 81.6083 43.5026 81.2935 43.5026 80.9052V71.2617C43.5026 70.8734 43.1878 70.5586 42.7995 70.5586Z"
        fill="#FCE4E5"
      />
      <path
        opacity="0.6"
        d="M42.7995 70.5586H41.0371C40.6488 70.5586 40.334 70.8734 40.334 71.2617V80.9052C40.334 81.2935 40.6488 81.6083 41.0371 81.6083H42.7995C43.1878 81.6083 43.5026 81.2935 43.5026 80.9052V71.2617C43.5026 70.8734 43.1878 70.5586 42.7995 70.5586Z"
        fill="white"
      />
      <path
        d="M42.7995 114H41.0371C40.6488 114 40.334 114.315 40.334 114.703V124.347C40.334 124.735 40.6488 125.05 41.0371 125.05H42.7995C43.1878 125.05 43.5026 124.735 43.5026 124.347V114.703C43.5026 114.315 43.1878 114 42.7995 114Z"
        fill="#FCE4E5"
      />
      <path
        opacity="0.6"
        d="M42.7995 114H41.0371C40.6488 114 40.334 114.315 40.334 114.703V124.347C40.334 124.735 40.6488 125.05 41.0371 125.05H42.7995C43.1878 125.05 43.5026 124.735 43.5026 124.347V114.703C43.5026 114.315 43.1878 114 42.7995 114Z"
        fill="white"
      />
      <path
        d="M69.1504 114.004C78.097 114.004 85.3497 106.751 85.3497 97.8047C85.3497 88.8581 78.097 81.6055 69.1504 81.6055C60.2038 81.6055 52.9512 88.8581 52.9512 97.8047C52.9512 106.751 60.2038 114.004 69.1504 114.004Z"
        fill="#FCE4E5"
      />
      <path
        opacity="0.6"
        d="M69.1504 114.004C78.097 114.004 85.3497 106.751 85.3497 97.8047C85.3497 88.8581 78.097 81.6055 69.1504 81.6055C60.2038 81.6055 52.9512 88.8581 52.9512 97.8047C52.9512 106.751 60.2038 114.004 69.1504 114.004Z"
        fill="white"
      />
      <path
        d="M61.1367 89.1133L60.4648 89.7852L77.1731 106.493L77.8449 105.822L61.1367 89.1133Z"
        fill="#FCE4E5"
      />
      <path
        d="M77.1731 89.1159L60.4648 105.824L61.1367 106.496L77.8449 89.7878L77.1731 89.1159Z"
        fill="#FCE4E5"
      />
      <path
        opacity="0.2"
        d="M77.1165 89.0613L60.4082 105.77L61.08 106.441L77.7883 89.7331L77.1165 89.0613Z"
        fill="black"
      />
      <path
        opacity="0.2"
        d="M61.0859 89.0626L60.4141 89.7344L77.1223 106.443L77.7942 105.771L61.0859 89.0626Z"
        fill="black"
      />
      <path
        d="M69.1046 110.047C66.6719 110.048 64.2935 109.328 62.2703 107.977C60.2471 106.626 58.67 104.706 57.7384 102.458C56.8067 100.211 56.5625 97.7379 57.0365 95.3518C57.5106 92.9657 58.6816 90.7738 60.4014 89.0532C62.1213 87.3327 64.3128 86.1608 66.6987 85.6859C69.0846 85.2109 71.5578 85.4542 73.8054 86.385C76.0531 87.3157 77.9742 88.8921 79.3259 90.9148C80.6775 92.9375 81.399 95.3156 81.399 97.7483C81.3964 101.009 80.1005 104.135 77.7955 106.441C75.4906 108.746 72.3649 110.044 69.1046 110.047ZM69.1046 86.3993C66.8598 86.3984 64.6651 87.0632 62.7982 88.3097C60.9312 89.5561 59.4759 91.3283 58.6161 93.4019C57.7564 95.4756 57.531 97.7577 57.9684 99.9595C58.4057 102.161 59.4863 104.184 61.0733 105.772C62.6603 107.359 64.6824 108.441 66.884 108.879C69.0857 109.317 71.3678 109.093 73.4418 108.234C75.5159 107.375 77.2886 105.92 78.5359 104.054C79.7831 102.188 80.4489 99.9931 80.4489 97.7483C80.4463 94.74 79.2505 91.8555 77.1237 89.7278C74.9969 87.6002 72.113 86.4031 69.1046 86.3993Z"
        fill="#FCE4E5"
      />
      <path
        d="M72.738 101.388C74.7472 99.3788 74.7472 96.1212 72.738 94.1121C70.7288 92.1029 67.4713 92.1029 65.4622 94.1121C63.453 96.1212 63.453 99.3788 65.4622 101.388C67.4713 103.397 70.7288 103.397 72.738 101.388Z"
        fill="#FCE4E5"
      />
      <path
        opacity="0.2"
        d="M69.1046 110.047C66.6719 110.048 64.2935 109.328 62.2703 107.977C60.2471 106.626 58.67 104.706 57.7384 102.458C56.8067 100.211 56.5625 97.7379 57.0365 95.3518C57.5106 92.9657 58.6816 90.7738 60.4014 89.0532C62.1213 87.3327 64.3128 86.1608 66.6987 85.6859C69.0846 85.2109 71.5578 85.4542 73.8054 86.385C76.0531 87.3157 77.9742 88.8921 79.3259 90.9148C80.6775 92.9375 81.399 95.3156 81.399 97.7483C81.3964 101.009 80.1005 104.135 77.7955 106.441C75.4906 108.746 72.3649 110.044 69.1046 110.047ZM69.1046 86.3993C66.8598 86.3984 64.6651 87.0632 62.7982 88.3097C60.9312 89.5561 59.4759 91.3283 58.6161 93.4019C57.7564 95.4756 57.531 97.7577 57.9684 99.9595C58.4057 102.161 59.4863 104.184 61.0733 105.772C62.6603 107.359 64.6824 108.441 66.884 108.879C69.0857 109.317 71.3678 109.093 73.4418 108.234C75.5159 107.375 77.2886 105.92 78.5359 104.054C79.7831 102.188 80.4489 99.9931 80.4489 97.7483C80.4463 94.74 79.2505 91.8555 77.1237 89.7278C74.9969 87.6002 72.113 86.4031 69.1046 86.3993Z"
        fill="black"
      />
      <path
        opacity="0.2"
        d="M72.738 101.388C74.7472 99.3788 74.7472 96.1212 72.738 94.1121C70.7288 92.1029 67.4713 92.1029 65.4622 94.1121C63.453 96.1212 63.453 99.3788 65.4622 101.388C67.4713 103.397 70.7288 103.397 72.738 101.388Z"
        fill="black"
      />
      <path
        d="M29.3984 29.1484C30.5433 31.804 31.7262 34.4928 32.9233 37.1388C34.1204 39.7848 35.3461 42.4261 36.6572 44.9487C36.9945 45.5662 37.3175 46.2075 37.6691 46.7918L37.9256 47.2384L38.0586 47.4617C38.0453 47.4435 38.0293 47.4275 38.0111 47.4142C37.9541 47.3809 38.0111 47.4474 38.1251 47.5282C38.5611 47.7629 39.0331 47.9235 39.5218 48.0032C40.1502 48.1203 40.7845 48.2028 41.422 48.2503C42.7664 48.3405 44.1868 48.3405 45.6119 48.2978L47.7686 48.2075L49.9159 48.065L50.4717 50.6493C47.5377 51.6291 44.4928 52.2387 41.4077 52.464C40.5753 52.5175 39.7401 52.5096 38.9089 52.4402C37.9282 52.3739 36.9651 52.146 36.0586 51.7656C35.4308 51.4912 34.8664 51.0901 34.4007 50.5875C34.2613 50.4385 34.1327 50.2798 34.0159 50.1125L33.8496 49.8654L33.5266 49.3904C33.0753 48.7348 32.6953 48.0792 32.2867 47.4189C30.7476 44.7824 29.3699 42.1126 28.0778 39.4191C26.7856 36.7255 25.5837 34.0082 24.4531 31.2197L29.3984 29.1484Z"
        fill="#FACED0"
      />
      <path
        d="M23.0903 28.7845C22.0119 31.9389 28.3491 41.2166 28.3491 41.2166L34.4203 37.8533C33.3894 34.959 32.1723 32.1345 30.7766 29.3974C28.6342 25.3832 24.2732 25.3214 23.0903 28.7845Z"
        fill="#EE1132"
      />
      <path
        opacity="0.5"
        d="M23.0903 28.7845C22.0119 31.9389 28.3491 41.2166 28.3491 41.2166L34.4203 37.8533C33.3894 34.959 32.1723 32.1345 30.7766 29.3974C28.6342 25.3832 24.2732 25.3214 23.0903 28.7845Z"
        fill="white"
      />
      <path
        d="M22.9995 29.1799C24.3581 28.5291 25.9258 29.0754 27.5695 31.6787C29.2131 34.282 30.6193 38.6287 30.7761 39.8686L28.3486 41.213C28.3486 41.213 22.4959 32.643 22.9995 29.1799Z"
        fill="#EE1132"
      />
      <path
        opacity="0.4"
        d="M22.9995 29.1799C24.3581 28.5291 25.9258 29.0754 27.5695 31.6787C29.2131 34.282 30.6193 38.6287 30.7761 39.8686L28.3486 41.213C28.3486 41.213 22.4959 32.643 22.9995 29.1799Z"
        fill="white"
      />
      <path
        d="M27.1896 26.3739C27.1896 26.3739 31.712 37.0103 28.4484 56.8152H9.61741C9.38463 53.9222 10.7148 39.8179 7.45117 26.1934C9.52164 25.7902 11.6164 25.5236 13.7218 25.3953C16.3879 25.2432 19.0604 25.2432 21.7265 25.3953C23.5698 25.5817 25.3962 25.9089 27.1896 26.3739Z"
        fill="#EE1132"
      />
      <path
        opacity="0.5"
        d="M27.1896 26.3739C27.1896 26.3739 31.712 37.0103 28.4484 56.8152H9.61741C9.38463 53.9222 10.7148 39.8179 7.45117 26.1934C9.52164 25.7902 11.6164 25.5236 13.7218 25.3953C16.3879 25.2432 19.0604 25.2432 21.7265 25.3953C23.5698 25.5817 25.3962 25.9089 27.1896 26.3739Z"
        fill="white"
      />
      <path
        d="M11.6599 29.5567C12.0495 31.7989 11.5317 37.1575 9.60772 43.3617C9.45712 38.0462 8.83875 32.7543 7.75977 27.5472V27.5187C8.9949 27.3952 11.2847 27.4522 11.6599 29.5567Z"
        fill="#EE1132"
      />
      <path
        opacity="0.4"
        d="M11.6599 29.5567C12.0495 31.7989 11.5317 37.1575 9.60772 43.3617C9.45712 38.0462 8.83875 32.7543 7.75977 27.5472V27.5187C8.9949 27.3952 11.2847 27.4522 11.6599 29.5567Z"
        fill="white"
      />
      <path
        d="M2.94141 141.198L5.73471 143.697L11.0505 138.163L8.25723 135.664L2.94141 141.198Z"
        fill="#FACED0"
      />
      <path
        d="M6.50545 142.694L3.11358 140.423C3.05817 140.379 2.98952 140.355 2.91881 140.355C2.84809 140.355 2.77946 140.379 2.72404 140.423L0.130259 143.041C0.0796517 143.098 0.0423109 143.166 0.0208759 143.24C-0.000559096 143.313 -0.00556582 143.391 0.006219 143.466C0.0180038 143.542 0.0462924 143.614 0.0890718 143.678C0.131851 143.741 0.188074 143.795 0.25377 143.834C2.42 145.502 3.98293 146.76 5.21806 148.148C6.64322 149.768 7.4128 150 10.7382 150C12.2773 150 13.1467 148.703 12.5196 148.504C6.9805 146.751 8.46266 145.986 7.11351 143.416C6.97676 143.127 6.76723 142.878 6.50545 142.694Z"
        fill="#263238"
      />
      <path
        d="M11.0472 138.163L8.31088 141.018L5.51758 138.514L8.25388 135.664L11.0472 138.163Z"
        fill="#5E2B2B"
      />
      <path
        d="M12.966 138.242H16.4862L16.2486 142.446H12.7285L12.966 138.242Z"
        fill="#5E2B2B"
      />
      <path
        d="M28.4497 56.815C28.4497 56.815 32.1599 82.4963 33.4235 96.4723C34.4401 107.688 10.1888 141.127 10.1888 141.127L5.5 136.714C5.5 136.714 23.2099 106.733 21.8465 94.2491C20.5306 82.159 16.0509 56.8008 16.0509 56.8008L28.4497 56.815Z"
        fill="#EE1132"
      />
      <path
        opacity="0.5"
        d="M28.4497 56.815C28.4497 56.815 32.1599 82.4963 33.4235 96.4723C34.4401 107.688 10.1888 141.127 10.1888 141.127L5.5 136.714C5.5 136.714 23.2099 106.733 21.8465 94.2491C20.5306 82.159 16.0509 56.8008 16.0509 56.8008L28.4497 56.815Z"
        fill="black"
      />
      <path
        d="M12.5059 146.394H16.0307L16.4868 138.242H12.9667L12.5059 146.394Z"
        fill="#FACED0"
      />
      <path
        d="M16.216 145.992H12.2588C12.1928 145.99 12.1284 146.013 12.0795 146.058C12.0307 146.102 12.0012 146.164 11.9975 146.23L11.8028 149.36C11.8021 149.442 11.8176 149.523 11.8483 149.599C11.879 149.675 11.9243 149.744 11.9817 149.802C12.039 149.86 12.1073 149.907 12.1825 149.938C12.2578 149.97 12.3386 149.987 12.4203 149.988C13.798 149.964 14.4583 149.883 16.2207 149.883C17.2896 149.883 21.8406 149.997 23.3228 149.997C24.8049 149.997 24.7479 148.534 24.1303 148.401C21.3228 147.807 17.8407 146.976 16.843 146.206C16.6647 146.065 16.4433 145.99 16.216 145.992Z"
        fill="#263238"
      />
      <path
        d="M49.1756 48.224L52.4107 46.9414L53.1993 50.2668C53.1993 50.2668 49.5081 51.3689 48.9238 50.3048L49.1756 48.224Z"
        fill="#FACED0"
      />
      <path
        d="M55.1512 47.6112L56.1013 49.4307L53.1987 50.2573L52.4102 46.9414L55.1512 47.6112Z"
        fill="#FACED0"
      />
      <path
        d="M22.5071 25.4605L21.7375 25.3797C19.0714 25.2276 16.3989 25.2276 13.7329 25.3797L13.2578 25.4082L13.3006 25.5365C13.3386 25.6362 14.2222 27.9877 19.291 27.9877C21.5 27.9877 22.6543 27.622 22.9204 26.8286C22.9747 26.5843 22.9652 26.3301 22.8928 26.0904C22.8204 25.8508 22.6876 25.6339 22.5071 25.4605Z"
        fill="#E6E6E6"
      />
      <path
        d="M14.1958 16.3789C14.7896 18.9537 15.4594 23.6757 13.7207 25.4049C13.7207 25.4049 14.4713 27.5379 19.2598 27.5379C24.5234 27.5379 21.7253 25.4049 21.7253 25.4049C18.8275 24.7208 18.8323 22.5878 19.2598 20.5879L14.1958 16.3789Z"
        fill="#FACED0"
      />
      <path
        d="M16.2732 18.0938L19.2708 20.5735C19.1629 21.0418 19.0961 21.5187 19.0713 21.9987C17.9692 21.8419 16.4205 20.6353 16.2637 19.4856C16.1798 19.0252 16.1831 18.553 16.2732 18.0938Z"
        fill="#5E2B2B"
      />
      <path
        d="M21.7206 8.71412C23.3215 9.74973 23.5495 14.7283 21.2075 15.4409C18.8655 16.1534 18.6898 6.75691 21.7206 8.71412Z"
        fill="#263238"
      />
      <path
        d="M13.0851 13.6806C14.1587 17.006 14.6147 19.0155 16.781 20.3789C17.4798 20.828 18.2881 21.0775 19.1185 21.1004C19.9489 21.1234 20.7698 20.9188 21.4923 20.5088C22.2148 20.0989 22.8115 19.4992 23.2178 18.7747C23.6241 18.0501 23.8246 17.2282 23.7975 16.3979C23.7547 13.2008 22.0065 8.2793 18.3249 7.66173C17.5314 7.50857 16.7116 7.56655 15.9476 7.82987C15.1836 8.09319 14.5022 8.5526 13.9715 9.16212C13.4409 9.77163 13.0798 10.5098 12.9242 11.3029C12.7686 12.0959 12.8241 12.9158 13.0851 13.6806Z"
        fill="#FACED0"
      />
      <path
        d="M9.24634 10.5486C10.396 12.6341 13.2843 17.3846 14.6714 16.9095C16.0586 16.4345 16.0633 12.6341 14.2819 10.2256C14.2819 10.2256 28.3957 11.6507 28.6807 3.63184C28.9705 -4.5723 22.1725 3.36581 16.1916 5.66031C9.68339 8.17808 6.5433 5.64131 9.24634 10.5486Z"
        fill="#263238"
      />
      <path
        d="M9.84904 7.64121C9.82219 7.63576 9.79805 7.6212 9.78071 7.59999C9.76337 7.57878 9.75391 7.55222 9.75391 7.52483C9.75391 7.49743 9.76337 7.47088 9.78071 7.44967C9.79805 7.42846 9.82219 7.41389 9.84904 7.40844C10.0628 7.36094 10.2956 7.31818 10.5474 7.27068C12.44 6.96176 14.2854 6.4126 16.039 5.6365C17.8204 4.83841 19.6873 3.47026 21.3405 2.26363C22.3907 1.46185 23.4851 0.719604 24.6184 0.0403914C24.6463 0.026416 24.6785 0.0235487 24.7085 0.0323634C24.7385 0.0411782 24.764 0.0610194 24.7799 0.0878958C24.7874 0.101612 24.7922 0.116679 24.7938 0.132233C24.7955 0.147786 24.7941 0.163522 24.7897 0.17853C24.7853 0.193538 24.778 0.207527 24.7681 0.219692C24.7583 0.231857 24.7461 0.241957 24.7324 0.249415C23.607 0.923978 22.5206 1.66148 21.4783 2.45841C19.8204 3.66979 17.9392 5.04269 16.134 5.85502C14.3654 6.63837 12.504 7.19233 10.5949 7.50345C10.3431 7.55095 10.1198 7.59371 9.89654 7.64121H9.84904Z"
        fill="#263238"
      />
      <path
        d="M12.4961 17.7427C13.1332 18.3618 13.9831 18.7137 14.8714 18.726C16.0495 18.726 16.2965 17.6144 15.6647 16.6833C15.1041 15.8472 13.8215 14.8401 12.7859 15.2914C11.7503 15.7427 11.717 16.9778 12.4961 17.7427Z"
        fill="#FACED0"
      />
      <path
        opacity="0.2"
        d="M21.8489 94.2624C21.1696 88.0345 19.6494 78.2769 18.343 70.2866C19.0271 68.9469 20.9463 66.2344 20.9463 66.2344C22.666 72.1773 26.4046 94.5807 21.0698 124.837C15.5117 133.796 10.1911 141.14 10.1911 141.14L9.44531 129.587C14.5996 119.925 22.7943 102.894 21.8489 94.2624Z"
        fill="black"
      />
      <path
        d="M18.4977 13.4925C18.5689 13.768 18.7732 13.9675 18.9727 13.92C19.1722 13.8725 19.2435 13.6207 19.1722 13.3452C19.101 13.0697 18.8967 12.8701 18.6972 12.9224C18.4977 12.9747 18.4264 13.2169 18.4977 13.4925Z"
        fill="#263238"
      />
      <path
        d="M21.6312 12.8036C21.7024 13.0792 21.9067 13.2787 22.1062 13.2312C22.3057 13.1837 22.377 12.9366 22.3057 12.6611C22.2345 12.3856 22.035 12.1861 21.8307 12.2336C21.6264 12.2811 21.5599 12.5281 21.6312 12.8036Z"
        fill="#263238"
      />
      <path
        d="M20.6719 13.1602C21.2193 13.9308 21.8662 14.6256 22.5958 15.2266C22.4372 15.4029 22.2418 15.5423 22.0235 15.6351C21.8052 15.7278 21.5692 15.7716 21.3322 15.7634L20.6719 13.1602Z"
        fill="#630F0F"
      />
      <path
        d="M20.1346 16.7664C19.556 16.7722 18.9946 16.5699 18.5527 16.1964C18.5449 16.1889 18.5386 16.18 18.5343 16.1701C18.5299 16.1602 18.5276 16.1495 18.5274 16.1387C18.5271 16.1279 18.529 16.1171 18.533 16.1071C18.5369 16.097 18.5428 16.0878 18.5503 16.08C18.5578 16.0722 18.5667 16.066 18.5766 16.0616C18.5865 16.0573 18.5972 16.0549 18.608 16.0547C18.6188 16.0545 18.6295 16.0564 18.6396 16.0603C18.6497 16.0643 18.6589 16.0701 18.6667 16.0776C18.9448 16.3036 19.2705 16.4638 19.6193 16.546C19.9681 16.6283 20.3311 16.6306 20.6809 16.5527C20.6915 16.5502 20.7025 16.5498 20.7132 16.5516C20.724 16.5533 20.7343 16.5572 20.7436 16.5629C20.7528 16.5686 20.7609 16.5761 20.7672 16.585C20.7736 16.5938 20.7781 16.6038 20.7806 16.6144C20.7831 16.625 20.7835 16.636 20.7818 16.6468C20.78 16.6575 20.7762 16.6678 20.7704 16.6771C20.7647 16.6864 20.7572 16.6944 20.7483 16.7008C20.7395 16.7071 20.7295 16.7117 20.7189 16.7142C20.5267 16.7534 20.3307 16.7709 20.1346 16.7664Z"
        fill="#263238"
      />
      <path
        d="M17.2514 12.7007C17.2294 12.7052 17.2068 12.7052 17.1849 12.7007C17.1645 12.6925 17.1461 12.6802 17.1306 12.6646C17.1152 12.6489 17.1031 12.6303 17.0951 12.6099C17.0871 12.5894 17.0833 12.5675 17.0841 12.5456C17.0848 12.5236 17.09 12.5021 17.0994 12.4822C17.2007 12.2415 17.3593 12.0292 17.5613 11.8636C17.7633 11.6981 18.0026 11.5843 18.2585 11.5321C18.3023 11.5278 18.346 11.54 18.3812 11.5664C18.4164 11.5928 18.4404 11.6314 18.4485 11.6746C18.4519 11.6965 18.4508 11.7188 18.4454 11.7403C18.4401 11.7617 18.4305 11.7819 18.4172 11.7996C18.4039 11.8173 18.3872 11.8321 18.3682 11.8433C18.3491 11.8545 18.3279 11.8617 18.306 11.8646C18.1051 11.9099 17.918 12.0031 17.7609 12.1363C17.6037 12.2695 17.4811 12.4387 17.4034 12.6295C17.387 12.6541 17.3642 12.6738 17.3374 12.6864C17.3106 12.699 17.2808 12.7039 17.2514 12.7007Z"
        fill="#263238"
      />
      <path
        d="M22.6922 11.4423C22.6675 11.4425 22.6431 11.4371 22.6209 11.4264C22.5987 11.4156 22.5792 11.4 22.5639 11.3806C22.4303 11.2144 22.261 11.0803 22.0686 10.9882C21.8762 10.8961 21.6656 10.8484 21.4523 10.8485C21.4085 10.8512 21.3653 10.8369 21.3316 10.8086C21.298 10.7803 21.2766 10.7402 21.2718 10.6965C21.2691 10.6526 21.2834 10.6094 21.3117 10.5758C21.34 10.5422 21.3802 10.5208 21.4238 10.516C21.6909 10.5106 21.9556 10.5664 22.1977 10.6792C22.4398 10.792 22.6529 10.9588 22.8205 11.1668C22.8483 11.2017 22.8613 11.246 22.8569 11.2904C22.8525 11.3348 22.8309 11.3757 22.7967 11.4043C22.7673 11.4286 22.7304 11.442 22.6922 11.4423Z"
        fill="#263238"
      />
      <path
        d="M9.89357 45.2624C8.94737 44.9437 8.03927 44.5215 7.18577 44.0035C6.99993 43.8852 6.82785 43.7466 6.67272 43.5902C6.89124 42.5688 7.17627 41.5427 7.48505 40.5119C7.79384 39.481 8.17388 38.4264 8.56817 37.386C8.96246 36.3457 9.38051 35.31 9.81756 34.2839C10.0361 33.7709 10.2594 33.2578 10.4874 32.7495L11.1715 31.2721L6.42094 28.8398C6.10741 29.4337 5.85088 29.9562 5.59435 30.5215C5.33782 31.0868 5.1193 31.6331 4.86277 32.1937C4.39406 33.3212 3.9665 34.4629 3.58013 35.6188C2.77487 37.9787 2.20986 40.4138 1.8937 42.8871V43.0154V43.2624C1.88179 43.4332 1.88179 43.6047 1.8937 43.7755C1.91286 44.1203 1.97347 44.4617 2.07422 44.7921C2.26149 45.3745 2.56536 45.9128 2.96732 46.374C3.55854 47.018 4.26818 47.5422 5.05754 47.9179C6.27339 48.4914 7.55901 48.9034 8.8817 49.1435C11.2904 49.6012 13.7422 49.7924 16.1927 49.7136L16.4113 47.0771C14.2055 46.5983 12.0293 45.9924 9.89357 45.2624Z"
        fill="#FACED0"
      />
      <path
        d="M17.4659 146.597C17.1457 146.59 16.8272 146.549 16.5158 146.474C16.5015 146.469 16.4885 146.461 16.4778 146.45C16.4671 146.439 16.459 146.426 16.454 146.412C16.4518 146.397 16.4537 146.381 16.4596 146.367C16.4654 146.353 16.475 146.341 16.4873 146.331C16.6013 146.251 17.5989 145.571 18.0264 145.742C18.075 145.761 18.1179 145.792 18.1512 145.832C18.1844 145.872 18.2069 145.919 18.2165 145.97C18.2373 146.047 18.2387 146.127 18.2204 146.205C18.2022 146.282 18.1649 146.353 18.112 146.412C17.9264 146.551 17.6966 146.616 17.4659 146.597ZM16.7438 146.36C17.3756 146.483 17.8459 146.464 18.0122 146.303C18.0464 146.262 18.0695 146.213 18.0795 146.161C18.0895 146.108 18.0859 146.054 18.0692 146.004C18.0649 145.979 18.0544 145.955 18.0386 145.935C18.0228 145.915 18.0023 145.9 17.9789 145.89C17.7272 145.79 17.1001 146.122 16.7438 146.36Z"
        fill="#EE1132"
      />
      <path
        d="M16.5437 146.475C16.5314 146.479 16.518 146.479 16.5057 146.475C16.4938 146.469 16.4836 146.46 16.4762 146.449C16.4687 146.439 16.4641 146.426 16.4629 146.413C16.4629 146.37 16.3822 145.358 16.7385 145.016C16.783 144.972 16.8369 144.939 16.8962 144.919C16.9555 144.9 17.0186 144.894 17.0805 144.902C17.1501 144.905 17.2167 144.931 17.2701 144.975C17.3234 145.02 17.3603 145.081 17.375 145.149C17.4558 145.534 16.9332 146.266 16.5817 146.475H16.5437ZM17.0187 145.049C16.9862 145.049 16.9538 145.055 16.9236 145.067C16.8934 145.079 16.866 145.098 16.843 145.121C16.6473 145.462 16.5658 145.856 16.6102 146.247C16.9 146 17.2753 145.439 17.2183 145.173C17.2183 145.121 17.1803 145.064 17.0567 145.049H17.0187Z"
        fill="#EE1132"
      />
      <path
        d="M8.79102 144.6C8.16282 144.503 7.56307 144.271 7.03333 143.92C7.01775 143.913 7.00531 143.9 6.99833 143.885C6.99135 143.869 6.99028 143.851 6.99533 143.835C6.99984 143.819 7.00901 143.804 7.02168 143.793C7.03435 143.782 7.04997 143.775 7.06659 143.773C7.25661 143.773 8.92404 143.65 9.23757 144.03C9.26562 144.061 9.28518 144.098 9.29434 144.139C9.30349 144.18 9.30194 144.222 9.28983 144.262C9.27522 144.328 9.24516 144.388 9.20218 144.44C9.15919 144.491 9.10453 144.531 9.0428 144.557C8.96211 144.586 8.87683 144.601 8.79102 144.6ZM7.34212 143.916C8.10695 144.338 8.71027 144.524 8.98105 144.391C9.02022 144.375 9.05494 144.35 9.08225 144.317C9.10955 144.285 9.12862 144.247 9.13782 144.205C9.14615 144.191 9.15054 144.175 9.15054 144.158C9.15054 144.141 9.14615 144.125 9.13782 144.11C8.94304 143.92 8.02145 143.887 7.34212 143.916Z"
        fill="#EE1132"
      />
      <path
        d="M7.06956 143.932C7.05619 143.932 7.04312 143.928 7.03151 143.921C7.0199 143.915 7.01012 143.905 7.00305 143.894C6.99719 143.882 6.99414 143.869 6.99414 143.856C6.99414 143.843 6.99719 143.83 7.00305 143.818C7.03155 143.761 7.66337 142.474 8.47571 142.868C8.69899 142.977 8.71798 143.105 8.69898 143.196C8.61822 143.58 7.56361 143.927 7.07906 143.942L7.06956 143.932ZM8.16692 142.949C7.72988 142.949 7.35459 143.504 7.21682 143.761C7.72513 143.699 8.50421 143.381 8.55171 143.153C8.55171 143.105 8.51372 143.053 8.4092 143.001C8.33387 142.964 8.25072 142.946 8.16692 142.949Z"
        fill="#EE1132"
      />
      <path
        d="M15.8645 47.0355L18.7576 46.2422L19.1519 49.8953C19.1519 49.8953 16.3016 50.7504 15.5938 49.254L15.8645 47.0355Z"
        fill="#FACED0"
      />
      <path
        d="M15.8066 47.1139C16.4124 46.4569 16.7673 45.6075 16.809 44.7148L17.5501 45.1186C17.5501 45.1186 17.6973 46.7576 17.4076 47.2279C17.1178 47.6982 15.8066 47.1139 15.8066 47.1139Z"
        fill="#FACED0"
      />
      <path
        d="M20.1106 46.6032L19.9348 49.8953H19.151L18.752 46.2422L20.1106 46.6032Z"
        fill="#FACED0"
      />
      <path
        d="M5.21875 136.461L10.4776 141.411L12.1735 139.591L6.1546 134.109L5.21875 136.461Z"
        fill="#EE1132"
      />
      <path
        opacity="0.1"
        d="M5.21875 136.461L10.4776 141.411L12.1735 139.591L6.1546 134.109L5.21875 136.461Z"
        fill="black"
      />
      <path
        d="M21.7562 56.8133C21.7562 56.8133 22.0887 83.725 21.5186 93.8104C20.8963 104.851 17.761 141.434 17.761 141.434H11.3098C11.3098 141.434 11.5046 105.744 11.115 94.8127C10.6922 82.9364 9.59961 56.8086 9.59961 56.8086L21.7562 56.8133Z"
        fill="#EE1132"
      />
      <path
        opacity="0.5"
        d="M21.7562 56.8133C21.7562 56.8133 22.0887 83.725 21.5186 93.8104C20.8963 104.851 17.761 141.434 17.761 141.434H11.3098C11.3098 141.434 11.5046 105.744 11.115 94.8127C10.6922 82.9364 9.59961 56.8086 9.59961 56.8086L21.7562 56.8133Z"
        fill="black"
      />
      <path
        d="M11.0521 141.436H18.0211L18.4059 138.984L10.7148 139.369L11.0521 141.436Z"
        fill="#EE1132"
      />
      <path
        opacity="0.1"
        d="M11.0521 141.436H18.0211L18.4059 138.984L10.7148 139.369L11.0521 141.436Z"
        fill="black"
      />
      <path
        d="M7.45218 26.1941C4.12682 26.6691 0.720703 38.8827 0.720703 38.8827L8.73482 41.9325C8.73482 41.9325 10.749 36.7069 12.1267 32.6072C13.5708 28.3365 11.0103 25.6715 7.45218 26.1941Z"
        fill="#EE1132"
      />
      <path
        opacity="0.5"
        d="M7.45218 26.1941C4.12682 26.6691 0.720703 38.8827 0.720703 38.8827L8.73482 41.9325C8.73482 41.9325 10.749 36.7069 12.1267 32.6072C13.5708 28.3365 11.0103 25.6715 7.45218 26.1941Z"
        fill="white"
      />
    </svg>
  </div>
);

/* ─── Toggle Switch ─── */
const ToggleSwitch = ({ label, checked, error, onChange, id }) => (
  <>
    <label className="proposal-toggle" htmlFor={id}>
      <span>{label}</span>
      <div className="yes-no-group">
        <label className="radio-option">
          <input
            type="radio"
            name={id}
            checked={checked === true}
            onChange={() => onChange(true)}
          />
          <span className="radio-custom"></span>
          <span>Yes</span>
        </label>

        <label className="radio-option">
          <input
            type="radio"
            name={id}
            checked={checked === false}
            onChange={() => onChange(false)}
          />
          <span className="radio-custom"></span>
          <span>No</span>
        </label>
      </div>
    </label>
    {error && <p className="error-toggle-text">{error}</p>}
  </>
);

/* ─── Pending Documents Bar ─── */
const PendingDocumentsBar = ({ docs, onUploadClick }) => {
  if (!docs || docs.length === 0) return null;
  return (
    <div className="proposal-pending-docs">
      <span className="proposal-pending-docs__icon">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <rect
            x="3"
            y="3"
            width="18"
            height="18"
            rx="3"
            stroke="#dc2626"
            strokeWidth="2"
          />
          <path
            d="M8 12h8M12 8v8"
            stroke="#dc2626"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        Pending Documents:
      </span>
      {docs.map((doc, i) => (
        <React.Fragment key={doc}>
          {i > 0 && <span className="proposal-pending-docs__sep">|</span>}
          <span className="proposal-pending-docs__item">{doc}</span>
        </React.Fragment>
      ))}
      <button
        className="proposal-pending-docs__upload"
        onClick={onUploadClick}
        type="button"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.5 8.43794V12.9379C13.5 13.0871 13.4407 13.2302 13.3352 13.3357C13.2298 13.4412 13.0867 13.5004 12.9375 13.5004H0.5625C0.413316 13.5004 0.270242 13.4412 0.164753 13.3357C0.0592633 13.2302 0 13.0871 0 12.9379V8.43794C0 8.28876 0.0592633 8.14568 0.164753 8.04019C0.270242 7.9347 0.413316 7.87544 0.5625 7.87544C0.711684 7.87544 0.854758 7.9347 0.960247 8.04019C1.06574 8.14568 1.125 8.28876 1.125 8.43794V12.3754H12.375V8.43794C12.375 8.28876 12.4343 8.14568 12.5398 8.04019C12.6452 7.9347 12.7883 7.87544 12.9375 7.87544C13.0867 7.87544 13.2298 7.9347 13.3352 8.04019C13.4407 8.14568 13.5 8.28876 13.5 8.43794ZM4.33547 3.77341L6.1875 1.92068V8.43794C6.1875 8.58713 6.24676 8.7302 6.35225 8.83569C6.45774 8.94118 6.60082 9.00044 6.75 9.00044C6.89918 9.00044 7.04226 8.94118 7.14775 8.83569C7.25324 8.7302 7.3125 8.58713 7.3125 8.43794V1.92068L9.16453 3.77341C9.27008 3.87896 9.41323 3.93825 9.5625 3.93825C9.71177 3.93825 9.85492 3.87896 9.96047 3.77341C10.066 3.66786 10.1253 3.52471 10.1253 3.37544C10.1253 3.22618 10.066 3.08302 9.96047 2.97747L7.14797 0.164974C7.09573 0.112674 7.03369 0.0711849 6.9654 0.0428775C6.89712 0.0145701 6.82392 0 6.75 0C6.67608 0 6.60288 0.0145701 6.5346 0.0428775C6.46631 0.0711849 6.40427 0.112674 6.35203 0.164974L3.53953 2.97747C3.43398 3.08302 3.37469 3.22618 3.37469 3.37544C3.37469 3.52471 3.43398 3.66786 3.53953 3.77341C3.64508 3.87896 3.78823 3.93825 3.9375 3.93825C4.08677 3.93825 4.22992 3.87896 4.33547 3.77341Z"
            fill="#97262A"
          ></path>
        </svg>
        <span>Upload Now</span>
      </button>
    </div>
  );
};

/* ─── Discount Banner ─── */
const DiscountBanner = () => (
  <div className="proposal-discount-banner">
    Complete this journey online and get <strong>10% discount.</strong>
  </div>
);

const nomineeFields = [
  {
    fieldReference: "relationship",
    label: "Relationship",
    validation: { required: true },
  },

  {
    fieldReference: "titleOfNominee",
    label: "Title",
    validation: { required: true },
  },

  {
    fieldReference: "fullName",
    label: "Full Name",
    validation: { required: true },
  },

  {
    fieldReference: "mobileNo",
    label: "Mobile No.",
    validation: { required: true },
  },

  {
    fieldReference: "dateOfBirth",
    label: "Date of Birth",
    // Minor nominee is allowed.
    validation: { required: true, dobMinAge: 0, dobMaxAge: 80 },
  },

  { fieldReference: "gender", label: "Gender", validation: { required: true } },

  {
    fieldReference: "allocation",
    label: "Entitlement",
    validation: { required: true },
  },

  {
    fieldReference: "bankName",
    label: "Bank Name",
    validation: { required: true },
  },

  {
    fieldReference: "branchName",
    label: "Branch Name",
    validation: { required: true },
  },

  {
    fieldReference: "accountNumber",
    label: "Account Number",
    validation: { required: true, minLength: 9, maxLength: 18 },
  },

  {
    fieldReference: "ifscCode",
    label: "IFSC Code",
    validation: { required: true, regex: "^[A-Z]{4}0[A-Z0-9]{6}$" },
  },
  {
    fieldReference: "emailId",
    label: "Email ID",
    validation: {
      required: false,
      regex: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
      regexErrorMessage: "Please enter a valid email address",
    },
  },
  {
    fieldReference: "communicationAddressSameAsPermanent",
    label: "Communication Address Same As Permanent",
    validation: { required: true },
  },
  {
    fieldReference: "nomineeAddressSameAsProposer",
    label: "Nominee Address Same As Proposer",
    validation: { required: true },
  },
];

const isMinor = (dob) => {
  if (!dob) return false;

  const birthDate = new Date(dob);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age < 18;
};

const getNomineeFields = (nominee) => {
  const safeNominee = nominee && typeof nominee === "object" ? nominee : {};
  const fields = [...nomineeFields];

  const showAppointee = isMinor(nominee.dateOfBirth);

  if (showAppointee) {
    fields.push(
      {
        fieldReference: "appointeeRequired",
        label: "Appointee Required",
        validation: { required: true },
      },
      {
        fieldReference: "appointeeAddressSameAsNominee",
        label: "Appointee Address Same As Nominee",
        validation: { required: true },
      },
    );
  }

  if (safeNominee.appointeeRequired) {
    fields.push(
      {
        fieldReference: "appointeeRelationship",
        label: "Appointee Relationship",
        validation: { required: true },
      },

      {
        fieldReference: "appointeeTitle",
        label: "Title",
        validation: { required: true },
      },

      {
        fieldReference: "appointeeFullName",
        label: "Full Name",
        validation: { required: true },
      },

      {
        fieldReference: "appointeeMobileNo",
        label: "Mobile No",
        validation: { required: true },
      },

      {
        fieldReference: "appointeeDateOfBirth",
        label: "Date of Birth",
        // Appointee must be major.
        validation: { required: true, dobMinAge: 18, dobMaxAge: 80 },
      },

      {
        fieldReference: "appointeeBankName",
        label: "Bank Name",
        validation: { required: true },
      },

      {
        fieldReference: "appointeeBranchName",
        label: "Branch Name",
        validation: { required: true },
      },

      {
        fieldReference: "appointeeAccountNumber",
        label: "Account Number",
        validation: { required: true },
      },

      {
        fieldReference: "appointeeIfscCode",
        label: "IFSC Code",
        validation: { required: true },
      },
    );
  }

  if (
    safeNominee.appointeeRequired &&
    safeNominee.appointeeAddressSameAsNominee === false
  ) {
    fields.push(
      {
        fieldReference: "appointeeAddressLine1",
        label: "Address Line 1",
        validation: { required: true },
      },

      {
        fieldReference: "appointeeAddressLine2",
        label: "Address Line 2",
        validation: { required: true },
      },

      {
        fieldReference: "appointeePinCode",
        label: "Pin Code",
        validation: { required: true },
      },

      {
        fieldReference: "appointeeState",
        label: "State",
        validation: { required: true },
      },

      {
        fieldReference: "appointeeCity",
        label: "City",
        validation: { required: true },
      },
    );
  }

  if (safeNominee.communicationAddressSameAsPermanent === false) {
    fields.push(
      {
        fieldReference: "communicationAddressLine1",
        label: "Communication Address Line 1",
        validation: { required: true },
      },

      {
        fieldReference: "communicationAddressLine2",
        label: "Communication Address Line 2",
        validation: { required: true },
      },

      {
        fieldReference: "communicationPinCode",
        label: "Communication Pin Code",
        validation: { required: true },
      },

      {
        fieldReference: "communicationState",
        label: "Communication State",
        validation: { required: true },
      },

      {
        fieldReference: "communicationCity",
        label: "Communication City",
        validation: { required: true },
      },
    );
  }

  if (safeNominee.nomineeAddressSameAsProposer === false) {
    fields.push(
      {
        fieldReference: "mailingAddressLine1",
        label: "Address Line 1",
        validation: { required: true },
      },

      {
        fieldReference: "mailingAddressLine2",
        label: "Address Line 2",
        validation: { required: true },
      },

      {
        fieldReference: "pinCode",
        label: "Pin Code",
        validation: { required: true },
      },

      {
        fieldReference: "state",
        label: "State",
        validation: { required: true },
      },

      { fieldReference: "city", label: "City", validation: { required: true } },
    );
  }

  return fields;
};

const SALUTATION_GENDER_MAP = {
  Mr: "male",
  Sir: "male",
  Master: "male",

  Ms: "female",
  Miss: "female",
  Mrs: "female",
  Madam: "female",

  Doctor: "neutral",
  Professor: "neutral",
  Reverend: "neutral",
  "Rt Hon": "neutral",
};

const RELATION_GENDER_MAP = {
  Father: "male",
  "Grand Father": "male",
  Son: "male",
  Brother: "male",

  Mother: "female",
  "Grand Mother": "female",
  Daughter: "female",
  Sister: "female",

  Spouse: "neutral",
  Others: "neutral",
};

const getSalutationGender = (title) => {
  return SALUTATION_GENDER_MAP[title] || "neutral";
};

const getRelationGender = (relation) => {
  return RELATION_GENDER_MAP[relation] || "neutral";
};

/* ─── Single Nominee Form ─── */
const NomineeForm = ({
  data,
  errors = {},
  onChange,
  index,
  showSequenceError,
}) => {
  const apiCache = useSelector((state) => state.journey.apiCache || {});
  const [bankSearch, setBankSearch] = useState(data.bankName || "");
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [appointeeBankSearch, setAppointeeBankSearch] = useState(
    data.appointeeBankName || "",
  );

  const setField = useCallback(
    (field, value) => onChange(index, field, value),
    [index, onChange],
  );

  const applyAddressFields = useCallback(
    (updates) => {
      Object.entries(updates).forEach(([field, value]) => {
        onChange(index, field, value);
      });
    },
    [index, onChange],
  );

  const permanentAddress = useProposalAddressLocation({
    values: data,
    onChange: setField,
    applyFields: applyAddressFields,
  });
  const communicationAddress = useProposalAddressLocation({
    values: data,
    onChange: setField,
    applyFields: applyAddressFields,
    fieldNames: {
      pinCode: "communicationPinCode",
      state: "communicationState",
      city: "communicationCity",
    },
  });
  const appointeeAddress = useProposalAddressLocation({
    values: data,
    onChange: setField,
    applyFields: applyAddressFields,
    fieldNames: {
      pinCode: "appointeePinCode",
      state: "appointeeState",
      city: "appointeeCity",
    },
  });

  const [showAppointeeBankDropdown, setShowAppointeeBankDropdown] =
    useState(false);
  const sameAddress = data.communicationAddressSameAsPermanent;
  const sameAddressAsProposer = data.nomineeAddressSameAsProposer;
  const sameAddressAsNominee = data.appointeeAddressSameAsNominee;

  const lifeTabData = useSelector(
    (state) => state.journey.stepData.lifeAssured,
  );

  const bankOptions = useMemo(() => {
    return (apiCache.bankdetails?.data || []).map((item) => ({
      label: item.bankName,
      value: item.bankCode,
    }));
  }, [apiCache.bankdetails]);

  const filteredBanks = useMemo(() => {
    return bankOptions.filter((bank) =>
      bank.label.toLowerCase().includes(bankSearch.toLowerCase()),
    );
  }, [bankOptions, bankSearch]);

  const filteredAppointeeBanks = useMemo(() => {
    return bankOptions.filter((bank) =>
      bank.label.toLowerCase().includes(appointeeBankSearch.toLowerCase()),
    );
  }, [bankOptions, appointeeBankSearch]);

  const today = new Date();

  const maxDate = today;

  const maxDateN = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate(),
  );

  const minDate = new Date(
    today.getFullYear() - 80,
    today.getMonth(),
    today.getDate(),
  );

  const formatDate = (date) => {
    return date.toISOString().split("T")[0];
  };

  const handleChange = useCallback(
    (name, value) => {
      if (name === "accountNumber" || name === "appointeeAccountNumber") {
        value = value.replace(/\D/g, "");
      }
      if (name === "branchName" || name === "appointeeBranchName") {
        value = value.replace(/^\s+/, "").replace(/[^a-zA-Z0-9\s]/g, "");
      }
      if (name === "fullName" || name === "appointeeFullName") {
        value = value
          .replace(/^\s+/, "")
          .replace(/[^a-zA-Z\s]/g, "")
          .slice(0, 30);
      }
      onChange(index, name, value);
    },
    [index, onChange],
  );

  const handleSameAddressChange = (checked) => {
    if (checked) {
      applyAddressFields({
        communicationAddressSameAsPermanent: true,
        communicationAddressLine1: data.mailingAddressLine1 || "",
        communicationAddressLine2: data.mailingAddressLine2 || "",
        communicationAddressLine3: data.mailingAddressLine3 || "",
        communicationPinCode: data.pinCode || "",
        communicationState: data.state || "",
        communicationCity: data.city || "",
      });
    } else {
      applyAddressFields({
        communicationAddressSameAsPermanent: false,
        communicationAddressLine1: "",
        communicationAddressLine2: "",
        communicationAddressLine3: "",
        communicationPinCode: "",
        communicationState: "",
        communicationCity: "",
      });
    }
  };

  const handleSameAddressAsProposer = (checked) => {
    if (checked) {
      applyAddressFields({
        nomineeAddressSameAsProposer: true,
        mailingAddressLine1: lifeTabData?.addressLine1 || "",
        mailingAddressLine2: lifeTabData?.addressLine2 || "",
        mailingAddressLine3: lifeTabData?.addressLine3 || "",
        pinCode: lifeTabData?.pinCode || "",
        state: lifeTabData?.state || "",
        city: lifeTabData?.city || "",
      });
    } else {
      applyAddressFields({
        nomineeAddressSameAsProposer: false,
        mailingAddressLine1: "",
        mailingAddressLine2: "",
        mailingAddressLine3: "",
        pinCode: "",
        state: "",
        city: "",
      });
    }
  };

  const handleAppointeeSameAddress = (checked) => {
    if (checked) {
      applyAddressFields({
        appointeeAddressSameAsNominee: true,
        appointeeAddressLine1: data.mailingAddressLine1 || "",
        appointeeAddressLine2: data.mailingAddressLine2 || "",
        appointeeAddressLine3: data.mailingAddressLine3 || "",
        appointeePinCode: data.pinCode || "",
        appointeeState: data.state || "",
        appointeeCity: data.city || "",
      });
    } else {
      applyAddressFields({
        appointeeAddressSameAsNominee: false,
        appointeeAddressLine1: "",
        appointeeAddressLine2: "",
        appointeeAddressLine3: "",
        appointeePinCode: "",
        appointeeState: "",
        appointeeCity: "",
      });
    }
  };

  useEffect(() => {
    if (!sameAddressAsProposer) return undefined;

    const matchesProposer =
      (data.mailingAddressLine1 || "") === (lifeTabData?.addressLine1 || "") &&
      (data.mailingAddressLine2 || "") === (lifeTabData?.addressLine2 || "") &&
      (data.mailingAddressLine3 || "") === (lifeTabData?.addressLine3 || "") &&
      (data.pinCode || "") === (lifeTabData?.pinCode || "") &&
      (data.state || "") === (lifeTabData?.state || "") &&
      (data.city || "") === (lifeTabData?.city || "");

    if (matchesProposer) return undefined;

    applyAddressFields({
      mailingAddressLine1: lifeTabData?.addressLine1 || "",
      mailingAddressLine2: lifeTabData?.addressLine2 || "",
      mailingAddressLine3: lifeTabData?.addressLine3 || "",
      pinCode: lifeTabData?.pinCode || "",
      state: lifeTabData?.state || "",
      city: lifeTabData?.city || "",
    });
    return undefined;
  }, [
    sameAddressAsProposer,
    lifeTabData?.addressLine1,
    lifeTabData?.addressLine2,
    lifeTabData?.addressLine3,
    lifeTabData?.pinCode,
    lifeTabData?.state,
    lifeTabData?.city,
    data.mailingAddressLine1,
    data.mailingAddressLine2,
    data.mailingAddressLine3,
    data.pinCode,
    data.state,
    data.city,
    applyAddressFields,
  ]);

  useEffect(() => {
    if (!sameAddress) return undefined;

    const commMatchesMailing =
      (data.communicationAddressLine1 || "") ===
        (data.mailingAddressLine1 || "") &&
      (data.communicationAddressLine2 || "") ===
        (data.mailingAddressLine2 || "") &&
      (data.communicationAddressLine3 || "") ===
        (data.mailingAddressLine3 || "") &&
      (data.communicationPinCode || "") === (data.pinCode || "") &&
      (data.communicationState || "") === (data.state || "") &&
      (data.communicationCity || "") === (data.city || "");

    if (commMatchesMailing) return undefined;

    applyAddressFields({
      communicationAddressLine1: data.mailingAddressLine1 || "",
      communicationAddressLine2: data.mailingAddressLine2 || "",
      communicationAddressLine3: data.mailingAddressLine3 || "",
      communicationPinCode: data.pinCode || "",
      communicationState: data.state || "",
      communicationCity: data.city || "",
    });
    return undefined;
  }, [
    sameAddress,
    data.mailingAddressLine1,
    data.mailingAddressLine2,
    data.mailingAddressLine3,
    data.pinCode,
    data.state,
    data.city,
    data.communicationAddressLine1,
    data.communicationAddressLine2,
    data.communicationAddressLine3,
    data.communicationPinCode,
    data.communicationState,
    data.communicationCity,
    applyAddressFields,
  ]);

  useEffect(() => {
    if (!sameAddressAsNominee) return undefined;

    const appointeeMatchesNominee =
      (data.appointeeAddressLine1 || "") === (data.mailingAddressLine1 || "") &&
      (data.appointeeAddressLine2 || "") === (data.mailingAddressLine2 || "") &&
      (data.appointeeAddressLine3 || "") === (data.mailingAddressLine3 || "") &&
      (data.appointeePinCode || "") === (data.pinCode || "") &&
      (data.appointeeState || "") === (data.state || "") &&
      (data.appointeeCity || "") === (data.city || "");

    if (appointeeMatchesNominee) return undefined;

    applyAddressFields({
      appointeeAddressLine1: data.mailingAddressLine1 || "",
      appointeeAddressLine2: data.mailingAddressLine2 || "",
      appointeeAddressLine3: data.mailingAddressLine3 || "",
      appointeePinCode: data.pinCode || "",
      appointeeState: data.state || "",
      appointeeCity: data.city || "",
    });
    return undefined;
  }, [
    sameAddressAsNominee,
    data.mailingAddressLine1,
    data.mailingAddressLine2,
    data.mailingAddressLine3,
    data.pinCode,
    data.state,
    data.city,
    data.appointeeAddressLine1,
    data.appointeeAddressLine2,
    data.appointeeAddressLine3,
    data.appointeePinCode,
    data.appointeeState,
    data.appointeeCity,
    applyAddressFields,
  ]);

  const isMinor = (dob) => {
    if (!dob) return false;

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age < 18;
  };

  const showAppointee = isMinor(data.dateOfBirth);

  useEffect(() => {
    if (isMinor(data.dateOfBirth)) {
      handleChange("appointeeRequired", true);
    } else {
      handleChange("appointeeRequired", false);
      handleChange("appointeeRelationship", "");
      handleChange("appointeeTitle", "");
      handleChange("appointeeFullName", "");
      handleChange("appointeeMobileNo", "");
      handleChange("appointeeDateOfBirth", "");
    }
  }, [data.dateOfBirth, handleChange]);

  const relationshipOptions = useMemo(
    () => toNomineeRelationshipOptions(apiCache.relationshipdetails?.data),
    [apiCache.relationshipdetails],
  );

  const salutationOptions = useMemo(() => {
    const selectedRelation = data.relationship;
    const selectedGender = getRelationGender(selectedRelation);

    return (apiCache.salutationdetails?.data || [])
      .filter((item) => {
        const titleGender = getSalutationGender(item.salutationTitle);

        if (!selectedRelation) return true;
        if (selectedGender === "neutral") return true;

        if (titleGender === "neutral") return true;

        return titleGender === selectedGender;
      })
      .map((item) => ({
        label: item.salutationTitle,
        value: item.salutationTitle,
      }));
  }, [apiCache.salutationdetails, data.relationship]);

  const appointeeSalutationOptions = useMemo(() => {
    const selectedRelation = data.appointeeRelationship;
    const selectedGender = getRelationGender(selectedRelation);

    return (apiCache.salutationdetails?.data || [])
      .filter((item) => {
        const titleGender = getSalutationGender(item.salutationTitle);

        if (!selectedRelation) return true;
        if (selectedGender === "neutral") return true;

        if (titleGender === "neutral") return true;

        return titleGender === selectedGender;
      })
      .map((item) => ({
        label: item.salutationTitle,
        value: item.salutationTitle,
      }));
  }, [apiCache.salutationdetails, data.appointeeRelationship]);

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const itemRefs = useRef([]);

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();

      setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();

      setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === "PageDown") {
      e.preventDefault();

      setActiveIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === "PageUp") {
      e.preventDefault();

      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0) {
        e.preventDefault();
        handleSelect(suggestions[activeIndex]);
      }
    }
  };

  useEffect(() => {
    if (activeIndex >= 0) {
      itemRefs.current[activeIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [activeIndex]);

  const handleEmailChange = (name, val) => {
    if (!/^[a-zA-Z0-9@._-]*$/.test(val)) return;
    if (val.length > 50) return;
    // update parent
    onChange(index, name, val);

    const atIndex = val.indexOf("@");

    if (atIndex !== -1) {
      const typedDomain = val.slice(atIndex + 1).toLowerCase();

      const filtered = EMAIL_DOMAINS.filter((domain) =>
        domain.startsWith(typedDomain),
      );

      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };
  const handleSelect = (domain) => {
    const namePart = (data.emailId || "").split("@")[0];
    const newEmail = `${namePart}@${domain}`;
    if (newEmail.length > 50) {
      return;
    }

    onChange(index, "emailId", newEmail);

    setShowSuggestions(false);
  };

  const wrapperRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="proposal-nominee-form proposal-section">
      {showSequenceError && (
        <div className="proposal-error-banner">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 1.6875C7.55373 1.6875 6.13993 2.11637 4.9374 2.91988C3.73486 3.72339 2.7976 4.86544 2.24413 6.20163C1.69067 7.53781 1.54586 9.00811 1.82801 10.4266C2.11017 11.8451 2.80661 13.148 3.82928 14.1707C4.85196 15.1934 6.15492 15.8898 7.57341 16.172C8.99189 16.4541 10.4622 16.3093 11.7984 15.7559C13.1346 15.2024 14.2766 14.2651 15.0801 13.0626C15.8836 11.8601 16.3125 10.4463 16.3125 9C16.3105 7.06123 15.5394 5.20246 14.1685 3.83154C12.7975 2.46063 10.9388 1.68955 9 1.6875ZM11.648 10.852C11.7002 10.9043 11.7417 10.9663 11.77 11.0346C11.7983 11.1029 11.8128 11.1761 11.8128 11.25C11.8128 11.3239 11.7983 11.3971 11.77 11.4654C11.7417 11.5337 11.7002 11.5957 11.648 11.648C11.5957 11.7002 11.5337 11.7417 11.4654 11.77C11.3971 11.7983 11.3239 11.8128 11.25 11.8128C11.1761 11.8128 11.1029 11.7983 11.0346 11.77C10.9663 11.7417 10.9043 11.7002 10.852 11.648L9 9.79523L7.14797 11.648C7.09571 11.7002 7.03367 11.7417 6.96538 11.77C6.8971 11.7983 6.82391 11.8128 6.75 11.8128C6.67609 11.8128 6.60291 11.7983 6.53462 11.77C6.46634 11.7417 6.4043 11.7002 6.35203 11.648C6.29977 11.5957 6.25832 11.5337 6.23003 11.4654C6.20175 11.3971 6.18719 11.3239 6.18719 11.25C6.18719 11.1761 6.20175 11.1029 6.23003 11.0346C6.25832 10.9663 6.29977 10.9043 6.35203 10.852L8.20477 9L6.35203 7.14797C6.24649 7.04242 6.18719 6.89927 6.18719 6.75C6.18719 6.60073 6.24649 6.45758 6.35203 6.35203C6.45758 6.24648 6.60074 6.18719 6.75 6.18719C6.89927 6.18719 7.04242 6.24648 7.14797 6.35203L9 8.20477L10.852 6.35203C10.9043 6.29977 10.9663 6.25831 11.0346 6.23003C11.1029 6.20174 11.1761 6.18719 11.25 6.18719C11.3239 6.18719 11.3971 6.20174 11.4654 6.23003C11.5337 6.25831 11.5957 6.29977 11.648 6.35203C11.7002 6.40429 11.7417 6.46634 11.77 6.53462C11.7983 6.6029 11.8128 6.67609 11.8128 6.75C11.8128 6.82391 11.7983 6.8971 11.77 6.96538C11.7417 7.03366 11.7002 7.09571 11.648 7.14797L9.79524 9L11.648 10.852Z"
              fill="#DA1414"
            />
          </svg>
          Nominee details need to be added in sequence. Please complete Nominee{" "}
          {index} first.
        </div>
      )}

      {/* Basic Details */}
      <div className="proposal-form-grid">
        <h4 className="proposal-section__subtitle">Basic Details</h4>
        <div className="form-wraper">
          <FormField
            label="Relationship with you"
            halfWidth
            error={errors?.relationship}
          >
            <SelectInput
              name="relationship"
              value={data.relationship}
              onChange={handleChange}
              options={relationshipOptions}
            />
          </FormField>
          <FormField
            label="Title of Nominee"
            halfWidth
            error={errors?.titleOfNominee}
          >
            <SelectInput
              name="titleOfNominee"
              value={data.titleOfNominee}
              onChange={handleChange}
              options={salutationOptions}
            />
          </FormField>
          <FormField label="Full Name" halfWidth error={errors?.fullName}>
            <TextInput
              name="fullName"
              value={data.fullName}
              onChange={handleChange}
              placeholder="Enter full name"
              maxLength={20}
              autoComplete="name-full"
            />
          </FormField>
          <FormField label="Mobile No." halfWidth error={errors?.mobileNo}>
            <MobileInput
              name="mobileNo"
              value={data.mobileNo}
              onChange={handleChange}
            />
          </FormField>
          <FormField
            label="Date of Birth"
            halfWidth
            error={errors?.dateOfBirth}
          >
            <DateInput
              name="dateOfBirth"
              value={data.dateOfBirth}
              onChange={handleChange}
              min={formatDate(minDate)}
              max={formatDate(maxDate)}
              autoComplete="birth-date"
            />
          </FormField>
          <FormField label="Gender" halfWidth error={errors?.gender}>
            <GenderRadio
              name="gender"
              value={data.gender}
              onChange={handleChange}
            />
          </FormField>
          <FormField label="Email ID" halfWidth error={errors?.emailId}>
            <div ref={wrapperRef} className="proposal-autocomplete-wrapper">
              <TextInput
                name="emailId"
                value={data.emailId}
                onChange={handleEmailChange}
                placeholder="Enter email address"
                handleKeyDown={handleKeyDown}
                maxLength={50}
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className="proposal-autocomplete-dropdown">
                  {suggestions.map((domain, index) => (
                    <div
                      key={domain}
                      ref={(el) => (itemRefs.current[index] = el)}
                      className={`proposal-autocomplete-option ${
                        activeIndex === index ? "active" : ""
                      }`}
                      onMouseDown={() => handleSelect(domain)}
                    >
                      {(data.emailId || "").split("@")[0]}@{domain}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </FormField>
          <FormField label="Entitlement" halfWidth error={errors?.allocation}>
            <AllocationInput
              name="allocation"
              value={data.allocation}
              onChange={handleChange}
            />
          </FormField>
        </div>
      </div>

      {/* Residential Details */}
      <div className="proposal-form-grid">
        <h4 className="proposal-section__subtitle">Residential Details</h4>
        <div className="form-wraper">
          <div className="proposal-toggle-section bg-shadow-box">
            <div className="proposal-toggle-section-list">
              <ToggleSwitch
                label={TEXT_CONTENT.NOM_TOGGLE}
                checked={sameAddressAsProposer}
                error={errors?.nomineeAddressSameAsProposer}
                onChange={handleSameAddressAsProposer}
                id={`nomineeAddressSameAsProposer-${index}`}
              />
              {data.nomineeAddressSameAsProposer === false && (
                <div className="proposal-resulting-fields">
                  <div className="form-wraper">
                    <FormField
                      label="Address Line 1"
                      halfWidth
                      error={errors?.mailingAddressLine1}
                    >
                      <TextInput
                        name="mailingAddressLine1"
                        value={data.mailingAddressLine1}
                        // onChange={handleChange}
                        onChange={(name, value) => {
                          if (/^[a-zA-Z0-9\s.,'#\-\/()&]{0,30}$/.test(value)) {
                            onChange(index, name, value);
                          }
                        }}
                        maxLength={30}
                        autoComplete="Mailing-address-one"
                      />
                    </FormField>
                    <FormField
                      label="Address Line 2"
                      halfWidth
                      error={errors?.mailingAddressLine2}
                    >
                      <TextInput
                        name="mailingAddressLine2"
                        value={data.mailingAddressLine2}
                        // onChange={handleChange}
                        onChange={(name, value) => {
                          if (/^[a-zA-Z0-9\s.,'#\-\/()&]{0,30}$/.test(value)) {
                            onChange(index, name, value);
                          }
                        }}
                        maxLength={30}
                        autoComplete="Mailing-address-two"
                      />
                    </FormField>
                    <FormField label="Address Line 3" optional halfWidth>
                      <TextInput
                        name="mailingAddressLine3"
                        value={data.mailingAddressLine3}
                        // onChange={handleChange}
                        onChange={(name, value) => {
                          if (/^[a-zA-Z0-9\s.,'#\-\/()&]{0,30}$/.test(value)) {
                            onChange(index, name, value);
                          }
                        }}
                        maxLength={30}
                        autoComplete="Mailing-address-three"
                      />
                    </FormField>
                    <FormField
                      label="Pin Code"
                      halfWidth
                      error={errors?.pinCode}
                    >
                      <TextInput
                        name="pinCode"
                        value={data.pinCode}
                        onChange={permanentAddress.handlePinCodeChange}
                        maxLength={6}
                        autoComplete="code-pin-no"
                      />
                    </FormField>
                    <FormField label="State" halfWidth error={errors?.state}>
                      <SelectInput
                        name="state"
                        value={data.state}
                        onChange={permanentAddress.handleStateChange}
                        options={permanentAddress.stateOptions}
                      />
                    </FormField>
                    <FormField label="City" halfWidth error={errors?.city}>
                      <SelectInput
                        name="city"
                        value={data.city}
                        onChange={permanentAddress.handleCityChange}
                        options={permanentAddress.cityOptions}
                      />
                    </FormField>
                  </div>
                </div>
              )}
            </div>
            <div className="proposal-toggle-section-list">
              <ToggleSwitch
                label={TEXT_CONTENT.NOMINEE_TOGGLE}
                checked={sameAddress}
                error={errors?.communicationAddressSameAsPermanent}
                onChange={handleSameAddressChange}
                id={`communicationAddressSameAsPermanent-${index}`}
              />
              {data.communicationAddressSameAsPermanent === false && (
                <div className="form-wraper">
                  <FormField
                    label="Address Line 1"
                    halfWidth
                    error={errors?.communicationAddressLine1}
                  >
                    <TextInput
                      name="communicationAddressLine1"
                      autoComplete="commmm-Address-line-one"
                      value={data.communicationAddressLine1}
                      // onChange={handleChange}
                      onChange={(name, value) => {
                        if (/^[a-zA-Z0-9\s.,'#\-\/()&]{0,30}$/.test(value)) {
                          onChange(index, name, value);
                        }
                      }}
                      maxLength={30}
                    />
                  </FormField>
                  <FormField
                    label="Address Line 2"
                    halfWidth
                    error={errors?.communicationAddressLine2}
                  >
                    <TextInput
                      name="communicationAddressLine2"
                      autoComplete="commm-Address-line-two"
                      value={data.communicationAddressLine2}
                      // onChange={handleChange}
                      onChange={(name, value) => {
                        if (/^[a-zA-Z0-9\s.,'#\-\/()&]{0,30}$/.test(value)) {
                          onChange(index, name, value);
                        }
                      }}
                      maxLength={30}
                    />
                  </FormField>
                  <FormField label="Address Line 3" optional halfWidth>
                    <TextInput
                      name="communicationAddressLine3"
                      autoComplete="commm-Address-line-three"
                      value={data.communicationAddressLine3}
                      // onChange={handleChange}
                      onChange={(name, value) => {
                        if (/^[a-zA-Z0-9\s.,'#\-\/()&]{0,30}$/.test(value)) {
                          onChange(index, name, value);
                        }
                      }}
                      maxLength={30}
                    />
                  </FormField>
                  <FormField
                    label="Pin Code"
                    halfWidth
                    error={errors?.communicationPinCode}
                  >
                    <TextInput
                      name="communicationPinCode"
                      autoComplete="no-code-pin"
                      value={data.communicationPinCode}
                      onChange={communicationAddress.handlePinCodeChange}
                      maxLength={6}
                    />
                  </FormField>
                  <FormField
                    label="State"
                    halfWidth
                    error={errors?.communicationState}
                  >
                    <SelectInput
                      name="communicationState"
                      value={data.communicationState}
                      onChange={communicationAddress.handleStateChange}
                      options={communicationAddress.stateOptions}
                      placeholder="Select"
                    />
                  </FormField>
                  <FormField
                    label="City"
                    halfWidth
                    error={errors?.communicationCity}
                  >
                    <SelectInput
                      name="communicationCity"
                      value={data.communicationCity}
                      onChange={communicationAddress.handleCityChange}
                      options={communicationAddress.cityOptions}
                    />
                  </FormField>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bank Details */}
      <div className="proposal-form-grid overflow-hidden-grid">
        <h4 className="proposal-section__subtitle">Bank Details</h4>
        <div className="form-wraper">
          <FormField label="Name of Bank" halfWidth error={errors?.bankName}>
            <div className="proposal-autocomplete-wrapper">
              <TextInput
                name="bankName"
                // value={bankSearch}
                value={data?.bankName}
                onChange={(name, value) => {
                  setBankSearch(value);
                  handleChange("bankName", value);
                  setShowBankDropdown(true);
                }}
                placeholder="Search bank"
                onFocus={() => setShowBankDropdown(true)}
                onBlur={() => setShowBankDropdown(false)}
              />

              {showBankDropdown && filteredBanks.length > 0 && (
                <div className="proposal-autocomplete-dropdown">
                  {filteredBanks.slice(0, 10).map((bank) => (
                    <div
                      key={bank.value}
                      className="proposal-autocomplete-option"
                      onMouseDown={() => {
                        setBankSearch(bank.label);
                        handleChange("bankName", bank.label);
                        setShowBankDropdown(false);
                      }}
                    >
                      {bank.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </FormField>
          <FormField label="Branch Name" halfWidth error={errors?.branchName}>
            <TextInput
              name="branchName"
              value={data.branchName}
              onChange={handleChange}
              autoComplete="Name-branch-nine"
              maxLength={20}
            />
          </FormField>
          <FormField
            label="Account Number"
            halfWidth
            error={errors?.accountNumber}
          >
            <TextInput
              name="accountNumber"
              autoComplete="Number-acc-one"
              value={data.accountNumber}
              onChange={handleChange}
              minLength={9}
              maxLength={18}
            />
          </FormField>
          <FormField label="IFSC Code" halfWidth error={errors?.ifscCode}>
            <TextInput
              name="ifscCode"
              autoComplete="code-ifscc"
              value={data.ifscCode}
              maxLength={11}
              onChange={(name, value) => {
                const upperValue = value.toUpperCase().trim();
                if (/^[A-Z0-9]*$/.test(upperValue)) {
                  handleChange(name, upperValue);
                }
              }}
              placeholder="e.g. HDFC0001234"
            />
          </FormField>
        </div>
      </div>
      {showAppointee && (
        <>
          <div className="seprater"></div>
          <div className="proposal-form-grid">
            <h4 className="proposal-section__subtitle">
              Appointee Basic Details (Nominee is Minor)
            </h4>
            <div className="form-wraper">
              <FormField
                label="Relationship with Nominee"
                halfWidth
                error={errors?.appointeeRelationship}
              >
                <SelectInput
                  name="appointeeRelationship"
                  autoComplete="Relation-appoint"
                  value={data.appointeeRelationship}
                  onChange={handleChange}
                  options={relationshipOptions}
                />
              </FormField>

              <FormField label="Title" halfWidth error={errors?.appointeeTitle}>
                <SelectInput
                  name="appointeeTitle"
                  autoComplete="title-appoint"
                  value={data.appointeeTitle}
                  onChange={handleChange}
                  options={appointeeSalutationOptions}
                />
              </FormField>

              <FormField
                label="Full Name"
                halfWidth
                error={errors?.appointeeFullName}
              >
                <TextInput
                  name="appointeeFullName"
                  value={data.appointeeFullName}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  maxLength={30}
                  autoComplete="appoint-name-full"
                />
              </FormField>

              <FormField
                label="Mobile No."
                halfWidth
                error={errors?.appointeeMobileNo}
              >
                <MobileInput
                  name="appointeeMobileNo"
                  autoComplete="Mobile-no-appointe"
                  value={data.appointeeMobileNo}
                  onChange={handleChange}
                />
              </FormField>

              <FormField
                label="Date of Birth"
                halfWidth
                error={errors?.appointeeDateOfBirth}
              >
                <DateInput
                  name="appointeeDateOfBirth"
                  value={data.appointeeDateOfBirth}
                  onChange={handleChange}
                  min={formatDate(minDate)}
                  max={formatDate(maxDateN)}
                  autoComplete="appoint-Birth-of-date"
                />
              </FormField>
            </div>
          </div>

          <div className="proposal-form-grid">
            <h4 className="proposal-section__subtitle">
              Appointee Residential Details
            </h4>

            <div className="proposal-address-toggle">
              <ToggleSwitch
                label="Appointee Address same as Nominee"
                error={errors?.appointeeAddressSameAsNominee}
                checked={sameAddressAsNominee}
                onChange={handleAppointeeSameAddress}
                id={`sameAddress-appointee-${index}`}
              />
            </div>

            {data.appointeeAddressSameAsNominee === false && (
              <div className="proposal-form-grid">
                <div className="form-wraper">
                  <FormField
                    label="Address Line 1"
                    halfWidth
                    error={errors?.appointeeAddressLine1}
                  >
                    <TextInput
                      name="appointeeAddressLine1"
                      value={data.appointeeAddressLine1}
                      maxLength={30}
                      // onChange={handleChange}
                      onChange={(name, value) => {
                        if (/^[a-zA-Z0-9\s.,'#\-\/()&]{0,30}$/.test(value)) {
                          onChange(index, name, value);
                        }
                      }}
                    />
                  </FormField>

                  <FormField
                    label="Address Line 2"
                    halfWidth
                    error={errors?.appointeeAddressLine2}
                  >
                    <TextInput
                      name="appointeeAddressLine2"
                      value={data.appointeeAddressLine2}
                      maxLength={30}
                      // onChange={handleChange}
                      onChange={(name, value) => {
                        if (/^[a-zA-Z0-9\s.,'#\-\/()&]{0,30}$/.test(value)) {
                          onChange(index, name, value);
                        }
                      }}
                      autoComplete="appiont-Address-Line2"
                    />
                  </FormField>

                  <FormField label="Address Line 3" optional halfWidth>
                    <TextInput
                      name="appointeeAddressLine3"
                      value={data.appointeeAddressLine3}
                      maxLength={30}
                      // onChange={handleChange}
                      onChange={(name, value) => {
                        if (/^[a-zA-Z0-9\s.,'#\-\/()&]{0,30}$/.test(value)) {
                          onChange(index, name, value);
                        }
                      }}
                      autoComplete="appoint-Address-Line3"
                    />
                  </FormField>

                  <FormField
                    label="Pin Code"
                    halfWidth
                    error={errors?.appointeePinCode}
                  >
                    <TextInput
                      name="appointeePinCode"
                      value={data.appointeePinCode}
                      onChange={appointeeAddress.handlePinCodeChange}
                      maxLength={6}
                      autoComplete="appoint-code-pin"
                    />
                  </FormField>

                  <FormField
                    label="State"
                    halfWidth
                    error={errors?.appointeeState}
                  >
                    <SelectInput
                      name="appointeeState"
                      value={data.appointeeState}
                      onChange={appointeeAddress.handleStateChange}
                      options={appointeeAddress.stateOptions}
                    />
                  </FormField>

                  <FormField
                    label="City"
                    halfWidth
                    error={errors?.appointeeCity}
                  >
                    <SelectInput
                      name="appointeeCity"
                      value={data.appointeeCity}
                      onChange={appointeeAddress.handleCityChange}
                      options={appointeeAddress.cityOptions}
                    />
                  </FormField>
                </div>
              </div>
            )}
          </div>

          <div className="proposal-form-grid">
            <h4 className="proposal-section__subtitle">
              Appointee Bank Details
            </h4>
            <div className="form-wraper">
              <FormField
                label="Name of Bank"
                halfWidth
                error={errors?.appointeeBankName}
              >
                <div className="proposal-autocomplete-wrapper">
                  <TextInput
                    name="appointeeBankName"
                    value={appointeeBankSearch}
                    onChange={(name, value) => {
                      setAppointeeBankSearch(value);
                      handleChange("appointeeBankName", value);
                      setShowAppointeeBankDropdown(true);
                    }}
                    placeholder="Search bank"
                    onFocus={() => setShowAppointeeBankDropdown(true)}
                    onBlur={() => setShowAppointeeBankDropdown(false)}
                  />

                  {showAppointeeBankDropdown &&
                    filteredAppointeeBanks.length > 0 && (
                      <div className="proposal-autocomplete-dropdown">
                        {filteredAppointeeBanks.slice(0, 10).map((bank) => (
                          <div
                            key={bank.value}
                            className="proposal-autocomplete-option"
                            onMouseDown={() => {
                              setAppointeeBankSearch(bank.label);
                              handleChange("appointeeBankName", bank.label);
                              setShowAppointeeBankDropdown(false);
                            }}
                          >
                            {bank.label}
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              </FormField>

              <FormField
                label="Branch Name"
                halfWidth
                error={errors?.appointeeBranchName}
              >
                <TextInput
                  name="appointeeBranchName"
                  value={data.appointeeBranchName}
                  onChange={handleChange}
                  autoComplete="appoint-BranchName"
                  maxLength={20}
                />
              </FormField>

              <FormField
                label="Account Number"
                halfWidth
                error={errors?.appointeeAccountNumber}
              >
                <TextInput
                  name="appointeeAccountNumber"
                  value={data.appointeeAccountNumber}
                  onChange={handleChange}
                  autoComplete="appointee-account-num"
                  minLength={9}
                  maxLength={18}
                />
              </FormField>
              <FormField
                label="IFSC Code"
                halfWidth
                error={errors?.appointeeIfscCode}
              >
                <TextInput
                  name="appointeeIfscCode"
                  value={data.appointeeIfscCode}
                  maxLength={11}
                  onChange={(name, value) => {
                    const upperValue = value.toUpperCase().trim();
                    if (/^[A-Z0-9]{0,11}$/.test(upperValue)) {
                      handleChange(name, upperValue);
                    }
                  }}
                  placeholder="e.g. HDFC0001234"
                  autoComplete="appoint-code-ifsc"
                />
              </FormField>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   NOMINEE TAB (MAIN)
   ═══════════════════════════════════════════════════ */
const NomineeTab = ({ onComplete, documents, setDocuments, onPersistStep }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiCache = useSelector((state) => state.journey.apiCache || {});
  const lifeAssuredForm = useSelector(
    (state) => state.journey.stepData?.lifeAssured || {},
  );
  const nomineeData = useSelector((state) => state.journey.stepData.nominee);
  const [nominees, setNominees] = useState(
    nomineeData?.nominees?.length
      ? normalizeNomineesList(sanitizeNomineeList(nomineeData.nominees))
      : [{ ...EMPTY_NOMINEE }],
  );
  const [loading, setLoading] = useState(false);
  const [activeNominee, setActiveNominee] = useState(
    nomineeData?.activeNominee ?? 0,
  );
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [errors, setErrors] = useState([]);
  const hasHydratedInitialNomineeRef = useRef(false);

  useEffect(() => {
    if (hasHydratedInitialNomineeRef.current) return;
    if (nomineeData?.nominees?.length) {
      const sanitizedNominees = normalizeNomineesList(
        sanitizeNomineeList(nomineeData.nominees),
      );
      setNominees(sanitizedNominees);
      const maxIndex = sanitizedNominees.length - 1;
      const hydratedIndex = Number.isInteger(nomineeData.activeNominee)
        ? nomineeData.activeNominee
        : 0;
      setActiveNominee(Math.max(0, Math.min(hydratedIndex, maxIndex)));
      hasHydratedInitialNomineeRef.current = true;
    }
  }, [nomineeData]);

  useEffect(() => {
    if (!Array.isArray(nominees) || nominees.length === 0) return;
    if (activeNominee < nominees.length) return;
    setActiveNominee(nominees.length - 1);
  }, [activeNominee, nominees]);

  const handleNomineeChange = useCallback((index, name, value) => {
    setNominees((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
    setErrors((prev) => {
      const updated = [...prev];
      if (updated[index]) {
        updated[index] = { ...updated[index], [name]: undefined };
      }
      return updated;
    });
  }, []);

  const addNominee = useCallback(() => {
    if (nominees.length < 5) {
      setNominees((prev) => [...prev, { ...EMPTY_NOMINEE, allocation: "" }]);
      setActiveNominee(nominees.length);
    }
  }, [nominees.length]);

  const removeNominee = useCallback(
    (index) => {
      if (nominees.length > 1) {
        setNominees((prev) => prev.filter((_, i) => i !== index));
        setActiveNominee((prev) =>
          prev >= index && prev > 0 ? prev - 1 : prev,
        );
      }
    },
    [nominees.length],
  );

  const leadData = useSelector((state) => state.journey?.leadFormData);
  const sumAssured = useSelector(
    (state) => state.journey?.quoteResult?.coverAmount,
  );
  const education = useSelector(
    (state) => state.journey?.leadFormData?.educationalLevel,
  );
  const organisationName =
    leadData?.organisation || leadData?.organization || "";
  const isOrgListed = useOrganisationListed(organisationName, apiCache);
  const _selectedIncomeProofLabel = getProposalIncomeProofPendingLabel(
    lifeAssuredForm?.incomeProof,
  );
  const showSurrogateIncomeDocs =
    (education === "Graduate" || education === "Post Graduate") &&
    Number(sumAssured) <= 20000000;

  const pendingDocs = [
    "Photograph",
    "Address Proof",
    "Age Proof",
    "Identification Proof",

    ...(isOrgListed
      ? ["Company ID Card (Front)", "Company ID Card (Back)"]
      : ["Financial/Income Documents"]),

    ...(showSurrogateIncomeDocs ? ["Surrogate Income Documents"] : []),
  ].filter((docLabel) => {
    // LifeAssured may save into alternate keys depending on employer/selection.
    // We mirror the same "hasUploaded" satisfaction rules so pending docs
    // update consistently across tabs.
    if (!documents) return true;
    if (docLabel === "Photograph")
      return !isProposalDocumentSlotFilled(documents.photograph);
    if (docLabel === "Age Proof")
      return !isProposalDocumentSlotFilled(documents.ageProof);
    if (docLabel === "Financial/Income Documents")
      return !isProposalDocumentSlotFilled(documents.incomeProof);
    if (docLabel === "Surrogate Income Documents") {
      return !isProposalDocumentSlotFilled(documents.surrogateIncomeProof);
    }
    if (docLabel === "Identification Proof") {
      return !isProposalDocumentSlotFilled(
        documents.identificationProof || documents.idProof,
      );
    }
    if (docLabel === "Address Proof") {
      return !(
        isProposalDocumentSlotFilled(documents.addressProof) ||
        isProposalDocumentSlotFilled(documents.addressProofPermanent) ||
        isProposalDocumentSlotFilled(documents.addressProofCommunication)
      );
    }
    if (docLabel === "Company ID Card (Front)") {
      return !isProposalDocumentSlotFilled(documents.companyIdFront);
    }
    if (docLabel === "Company ID Card (Back)") {
      return !isProposalDocumentSlotFilled(documents.companyIdBack);
    }
    if (docLabel === "Surrogate Income Documents") {
      return !isProposalDocumentSlotFilled(documents.surrogateIncomeProof);
    }
    return true;
  });

  // Prefill the modal with the already-uploaded docs.
  // LifeAssured may store alternate slot ids (e.g. addressProofPermanent/idProof),
  // so we map them into the standard modal slot ids.
  const initialFiles = useMemo(() => {
    const addressProof =
      documents?.addressProof ||
      documents?.addressProofPermanent ||
      documents?.addressProofCommunication ||
      null;
    const identificationProof =
      documents?.identificationProof || documents?.idProof || null;

    const baseFiles = {
      ...(documents?.photograph ? { photograph: documents.photograph } : {}),
      ...(documents?.ageProof ? { ageProof: documents.ageProof } : {}),
      ...(addressProof ? { addressProof } : {}),
      ...(identificationProof ? { identificationProof } : {}),
      ...(documents?.fatcaDeclaration
        ? { fatcaDeclaration: documents.fatcaDeclaration }
        : {}),
    };

    if (isOrgListed) {
      return {
        ...baseFiles,
        ...(documents?.companyIdFront
          ? { companyIdFront: documents.companyIdFront }
          : {}),
        ...(documents?.companyIdBack
          ? { companyIdBack: documents.companyIdBack }
          : {}),
        ...(documents?.surrogateIncomeProof
          ? { surrogateIncomeProof: documents.surrogateIncomeProof }
          : {}),
      };
    }
    return {
      ...baseFiles,
      ...(documents?.incomeProof ? { incomeProof: documents.incomeProof } : {}),
      ...(documents?.surrogateIncomeProof
        ? { surrogateIncomeProof: documents.surrogateIncomeProof }
        : {}),
    };
  }, [documents, isOrgListed]);

  const handleContinue = useCallback(async () => {
    const safeNominees = sanitizeNomineeList(nominees);
    const nomineeErrors = safeNominees.map((nominee) => {
      const fieldsToValidate = getNomineeFields(nominee);
      return validateForm(nominee, fieldsToValidate);
    });

    const totalAllocation = safeNominees.reduce(
      (sum, n) => sum + Number(n.allocation || 0),
      0,
    );

    if (totalAllocation !== 100) {
      nomineeErrors.forEach((err) => {
        err.allocation = "Total nominee entitlement must equal 100%";
      });
    }

    setErrors(nomineeErrors);

    const firstInvalid = nomineeErrors.findIndex(
      (err) => Object.keys(err).length > 0,
    );

    if (firstInvalid !== -1) {
      setActiveNominee(firstInvalid);
      return;
    }
    const nomineesWithBizOps = enrichNomineesWithBizOpsCodes(
      normalizeNomineesList(safeNominees),
      apiCache,
    );
    if (onPersistStep) {
      setLoading(true);
      try {
        const saved = await onPersistStep("nominee", {
          tab: "nominee",
          activeNominee,
          nominees: nomineesWithBizOps,
        });
        if (!saved) {
          return;
        }
      } finally {
        setLoading(false);
      }
    }
    onComplete?.({ activeNominee, nominees: nomineesWithBizOps });
    navigate("/proposal", {
      state: {
        tab: "medical",
        section: "lifestyle",
      },
      replace: true,
    });
  }, [onPersistStep, onComplete, activeNominee, nominees, apiCache]);

  const isPreviousNomineeIncomplete = (index) => {
    if (index === 0) return false;

    const prevNominee =
      nominees[index - 1] && typeof nominees[index - 1] === "object"
        ? nominees[index - 1]
        : { ...EMPTY_NOMINEE };
    const fieldsToValidate = getNomineeFields(prevNominee);
    const prevErrors = validateForm(prevNominee, fieldsToValidate);

    return Object.keys(prevErrors).length > 0;
  };

  return (
    <div className="proposal-tab-layout">
      {loading && (
        <div
          className="quote-premium-loader-overlay"
          aria-live="polite"
          aria-busy="true"
        >
          <BrandLoader color="#97262A" loading={true} size={15} />
        </div>
      )}
      <aside className="proposal-side">
        <NomineeIllustration />
        <h3 className="proposal-side__heading nominee-heading">
          {TEXT_CONTENT.NOM_HEADING}
        </h3>
        <p className="proposal-side__subtext">{TEXT_CONTENT.NOM_SUBHEADING}</p>
      </aside>

      <div className="proposal-tab-main">
      

        {/* Nominee Tabs */}
        <div className="proposal-nominee-tabs">
          <div className="proposal-nominee-tabs-wraper">
            {nominees.map((_, index) => (
              <button
                key={index}
                className={`proposal-nominee-tab ${activeNominee === index ? "active" : ""}`}
                onClick={() => setActiveNominee(index)}
              >
                Nominee {index + 1}
                {nominees.length > 1 && (
                  <span
                    className="proposal-nominee-tab__remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeNominee(index);
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Remove Nominee ${index + 1}`}
                  >
                    <svg
                      width="13"
                      height="14"
                      viewBox="0 0 13 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.75 2.5H9.5V1.75C9.5 1.28587 9.31563 0.840752 8.98744 0.512563C8.65925 0.184374 8.21413 0 7.75 0H4.75C4.28587 0 3.84075 0.184374 3.51256 0.512563C3.18437 0.840752 3 1.28587 3 1.75V2.5H0.75C0.551088 2.5 0.360322 2.57902 0.21967 2.71967C0.0790177 2.86032 0 3.05109 0 3.25C0 3.44891 0.0790177 3.63968 0.21967 3.78033C0.360322 3.92098 0.551088 4 0.75 4H1V12.5C1 12.8315 1.1317 13.1495 1.36612 13.3839C1.60054 13.6183 1.91848 13.75 2.25 13.75H10.25C10.5815 13.75 10.8995 13.6183 11.1339 13.3839C11.3683 13.1495 11.5 12.8315 11.5 12.5V4H11.75C11.9489 4 12.1397 3.92098 12.2803 3.78033C12.421 3.63968 12.5 3.44891 12.5 3.25C12.5 3.05109 12.421 2.86032 12.2803 2.71967C12.1397 2.57902 11.9489 2.5 11.75 2.5ZM4.5 1.75C4.5 1.6837 4.52634 1.62011 4.57322 1.57322C4.62011 1.52634 4.6837 1.5 4.75 1.5H7.75C7.8163 1.5 7.87989 1.52634 7.92678 1.57322C7.97366 1.62011 8 1.6837 8 1.75V2.5H4.5V1.75ZM10 12.25H2.5V4H10V12.25ZM5.5 6V10C5.5 10.1989 5.42098 10.3897 5.28033 10.5303C5.13968 10.671 4.94891 10.75 4.75 10.75C4.55109 10.75 4.36032 10.671 4.21967 10.5303C4.07902 10.3897 4 10.1989 4 10V6C4 5.80109 4.07902 5.61032 4.21967 5.46967C4.36032 5.32902 4.55109 5.25 4.75 5.25C4.94891 5.25 5.13968 5.32902 5.28033 5.46967C5.42098 5.61032 5.5 5.80109 5.5 6ZM8.5 6V10C8.5 10.1989 8.42098 10.3897 8.28033 10.5303C8.13968 10.671 7.94891 10.75 7.75 10.75C7.55109 10.75 7.36032 10.671 7.21967 10.5303C7.07902 10.3897 7 10.1989 7 10V6C7 5.80109 7.07902 5.61032 7.21967 5.46967C7.36032 5.32902 7.55109 5.25 7.75 5.25C7.94891 5.25 8.13968 5.32902 8.28033 5.46967C8.42098 5.61032 8.5 5.80109 8.5 6Z"
                        fill="#DA1414"
                      />
                    </svg>
                  </span>
                )}
              </button>
            ))}
          </div>
          {nominees.length < 5 && (
            <button
              className="proposal-nominee-btn btn tertiary-button regular add"
              onClick={addNominee}
            >
              + Add More Nominee
            </button>
          )}
        </div>

        {/* Active Nominee Form */}
        <NomineeForm
          data={nominees[activeNominee]}
          errors={errors[activeNominee] || {}}
          onChange={handleNomineeChange}
          index={activeNominee}
          showSequenceError={isPreviousNomineeIncomplete(activeNominee)}
        />

        <PendingDocumentsBar
          docs={pendingDocs}
          onUploadClick={() => setShowUploadModal(true)}
        />

        <div className="proposal-form-actions">
          <button
            className="proposal-btn-continue proposal-btn-continue btn primary-button regular"
            onClick={() => {
              void handleContinue();
            }}
          >
            Continue
          </button>
        </div>
      </div>

      <UploadDocumentsModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        documentFormContext={{
          formData: lifeAssuredForm,
          apiCache,
        }}
        initialFiles={initialFiles}
        onUploadComplete={({ files: uploadedFiles, removedSlotIds }) => {
          const updatedDocs = {
            ...documents,
            ...uploadedFiles,
          };

          // If user removes a "mapped" modal slot, remove all underlying keys that
          // could satisfy that document in the shared documents object.
          (removedSlotIds || []).forEach((slotId) => {
            if (slotId === "addressProof") {
              delete updatedDocs.addressProof;
              delete updatedDocs.addressProofPermanent;
              delete updatedDocs.addressProofCommunication;
              return;
            }
            if (slotId === "identificationProof") {
              delete updatedDocs.identificationProof;
              delete updatedDocs.idProof;
              return;
            }
            delete updatedDocs[slotId];
          });

          setDocuments(updatedDocs);

          dispatch(journeyActions.setDocuments(updatedDocs));
        }}
      />
    </div>
  );
};

export default NomineeTab;
